import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { KeyboardArrowUp } from "@mui/icons-material";
import {
    BugReport as BugReportIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import PodcastsOutlinedIcon from "@mui/icons-material/PodcastsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { CircularProgress } from "@mui/material";
import {
    FormControlLabel,
    FormHelperText,
    IconButton,
    Stack,
    styled,
    Switch,
    Tooltip,
    tooltipClasses,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DEV_MOCK_FB_ACCESS_TOKEN, DEV_MODE } from "Constant/app/process";
import { ButtonVariant } from "Constant/button";
import { routes } from "Constant/routes";
import FailedLeadsModal from "pages/LeadConnectionReportsPage/FailedLeadsModal";
import {
    buildDevMockDuplicateLeadsResponse,
    buildDevMockFailedLeadsResponse,
    withDevMockLeads,
} from "pages/LeadConnectionReportsPage/FailedLeadsModal/FailedLeadsModal.mock";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { getFacebookSourceIdentity } from "helpers/leads/facebookSourceField";
import {
    getDestinationIcon,
    getDestinationName,
    getSourceIcon,
    getSourceName,
} from "helpers/leads/icon";
import { getSourceApiDocument } from "helpers/leads/leadConnection/deleteConnection";
import { getFunctions } from "helpers/leads/leadConnection/getFunctions";
import {
    getDynamicFields,
    saveDynamicFields,
    updateConnectionComparedFields,
    updateNewConnectionField,
} from "helpers/leads/leadConnection/postConnectionField";
import { deleteDestinationWithConnection } from "helpers/leads/leadDestinations/deleteDestinations";
import { editDestinationByConnectionId } from "helpers/leads/leadDestinations/editDestinationByConnectionId";
import {
    getDestinationsByConnectionId,
    getLeadDestinationId,
    reloadDestinationFields,
} from "helpers/leads/leadDestinations/getDestinations";
import { addDestinationWithConnection } from "helpers/leads/leadDestinations/postDestinations";
import { editSourceByConnectionId } from "helpers/leads/leadSources/editSourceByConnectionId";
import { getSourcesByConnectionId } from "helpers/leads/leadSources/getSourcesByConnectionId";
import { sendFBInfosToServisAsync } from "helpers/leads/leadSources/sendFBInfosToServisAsync";
import { getDuplicateLeads, getFailedLeads } from "helpers/leads/reports";
import {
    getServicesAsync,
    saveFilters,
} from "helpers/leads/stepHelpers/sendStep1ToServis";
import { exportFieldMapping } from "helpers/leads/stepHelpers/step2Helpers";
import { reloadTiktokPages } from "helpers/tiktok-lead/tiktok-lead";

import EmptyState from "components/atoms/EmptyState";
import StandardText from "components/atoms/StandardText";
import StandartButton from "components/atoms/StandartButton";
import InlineConnectionNameEdit from "components/ConnectionNameEdit/InlineConnectionNameEdit";
import LeadConnectionFilter from "components/LeadConnectionFilter";
import FieldOptionsModal from "components/Leads/SharedComponents/FieldOptionsModal";
import FieldOptionsSummaryPopover from "components/Leads/SharedComponents/FieldOptionsSummaryPopover";
import LeadStaticField from "components/Leads/SharedComponents/LeadStaticField";
import SummaryPopover, {
    SummaryPopoverSection,
    SummaryPopoverValue,
} from "components/Leads/SharedComponents/SummaryPopover";
import MuiSelect from "components/molecules/MuiSelect";
import SourcePopover from "components/molecules/SourcePopover";
import CreateDynamicValueModal from "components/organisms/CreateDynamicValueModal";
import SwalAlertComp from "components/SwalAlert";

import PageContainer from "../../components/atoms/PageContainer";
import {
    getSingleConnectionRedux,
    refreshSource,
} from "../../redux/slices/leadConnection/leadConnection.asyncActions";
import {
    clearRefetchSourcePages,
    hydrateSingleConnectionFromCache,
} from "../../redux/slices/leadConnection/leadConnection.slice";
import { getAllLeadDestinationTypesAsync } from "../../redux/slices/leadDestinations/leadDestinations.asyncActions";
import { getAllLeadSourcesTypesAsync } from "../../redux/slices/leadSources/leadSources.asyncActions";
import { resetLastAddedService } from "../../redux/slices/leadsWizardSourceDestination/leadsWizardSourceDestination.slice";
import { LogoIcon } from "../shared/CardStyles";
import AddComparisationModal from "./AddComparisationModal";
import LeadIssueRow, { LeadIssueList } from "./components/LeadIssueRow";
import "./connectionDetail.css";
import ConnectionDetailReconnectButton from "./ConnectionDetailReconnectButton";
import DynamicFieldsSection from "./DynamicFieldsSection/exports";
import MappingDynamicSource from "./MappingSection/MappingDynamicSource";
import {
    mappingAutocompleteTextFieldSx,
    mappingReadonlyFieldSx,
} from "./MappingSection/mappingFieldStyles";
import MappingRowShell from "./MappingSection/MappingRowShell";
import ConnectionSectionHeader from "./shared/ConnectionSectionHeader";

const SourceErrorTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#000000",
        color: "#000",
        maxWidth: 220,
        fontSize: "12px",
        borderRadius: "10px",
        fontFamily: '"Nunito", serif',
        padding: "15px",
        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    },
    [`& .${tooltipClasses.tooltip} p`]: {
        fontWeight: "bold",
        fontFamily: '"Nunito", serif',
        color: "white",
        fontSize: "14px",
    },
    [`& .${tooltipClasses.tooltip} span`]: {
        fontWeight: "300",
        fontFamily: '"Nunito", serif',
        color: "white",
        fontSize: "12px",
    },
}));

const PulsingDot = styled("span")({
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#2e7d32",
    marginRight: "8px",
    animation: "pulse 1.5s ease-in-out infinite",
    "@keyframes pulse": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 },
    },
});

const ConnectionDetail = () => {
    const [sourceTypeName, setSourceTypeName] = useState("");
    const [pageNames, setPageNames] = useState([]); // TODO: To be removed @Ahmet
    const [formNames, setFormNames] = useState([]); // TODO: To be removed @Ahmet
    const [destinationPageName, setDestinationPageName] = useState("");
    const [destinationType, setDestinationType] = useState("");
    const [sourcesDetails, setSourcesDetails] = useState([]);
    const language = useSelector((state) => state.languagesState.languages);
    const [matchFields, setMatchFields] = useState([]);
    const [fieldEditPermission, setFieldEditPermission] = useState(false);
    const mappingEditSnapshotRef = useRef(null);
    const dynamicFieldDeletedDuringEditRef = useRef(false);
    const [isReloadingFields, setIsReloadingFields] = useState(false);
    const [fieldDestId, setFieldDestId] = useState({
        connection_id: "",
        destination_id: "",
    });
    const [fieldName, setFieldName] = useState([]);
    const [newLeadDestination, setNewLeadDestination] = useState(null);
    const [sourcePagesShow, setSourcePagesShow] = useState(false);
    const [destinationPagesShow, setDestinationPagesShow] = useState(false);
    const [connectionFilters, setConnectionFilters] = useState([]);
    const [selectedOperator, setSelectedOperator] = useState("");
    const [filterShowStatus, setFilterShowStatus] = useState(false);

    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [editFunctionsOptions, setEditFunctionsOptions] = useState([]);
    const [newFunctionsOptions, setNewFunctionsOptions] = useState([]);
    const [isDynamicValueOpen, setIsDynamicValueOpen] = useState(false);
    const [dynamicValueIndex, setDynamicValueIndex] = useState(null);
    const [dynamicFields, setDynamicFields] = useState([]);
    const [isDynamicFieldsLoading, setIsDynamicFieldsLoading] = useState(true);
    const [isDynamicFieldsModalOpen, setIsDynamicFieldsModalOpen] =
        useState(false);

    const [connectWebServicesList, setConnectWebServicesList] = useState([]);
    const [selectedWebService, setSelectedWebService] = useState("");
    const [destinationSelections, setDestinationSelections] = useState(null);
    const [zohoBtnHandler, setZohoBtnHandler] = useState(true);
    const [allZohoFields, setAllZohoFields] = useState([]);
    const [destinationSelectionCode, setDestinationSelectionCode] =
        useState("");
    const [businessIdState, setBusinessIdState] = useState("");
    const [isAddPageLoading, setIsAddPageLoading] = useState(false);
    const [selectedZohoField, setSelectedZohoField] = useState("");
    const [
        selectedZohoFieldChildrenOptions,
        setSelectedZohoFieldChildrenOptions,
    ] = useState([]);
    const [
        selectedZohoFieldChildrentoChildrenOptions,
        setSelectedZohoFieldChildrentoChildrenOptions,
    ] = useState([]);
    const [
        selectedZohoFieldChildrenToChildren,
        setSelectedZohoFieldChildrenToChildren,
    ] = useState("");
    const [selectedZohoFieldChildren, setSelectedZohoFieldChildren] =
        useState("");
    const [selectedWebServiceId, setSelectedWebServiceId] = useState();
    const [defaultWebServiceId, setDefaultWebServiceId] = useState();
    const [isSourcePopoverVisible, setIsSourcePopoverVisible] = useState();
    const [sourceCardRef, setSourceCardRef] = useState();
    const [isLoadingFailedLeads, setIsLoadingFailedLeads] = useState(false);
    const [failedLeads, setFailedLeads] = useState([]);
    const [isFailedLeadsModalOpen, setIsFailedLeadsModalOpen] = useState(false);
    const [isLoadingDuplicateLeads, setIsLoadingDuplicateLeads] =
        useState(false);
    const [duplicateLeads, setDuplicateLeads] = useState([]);
    const [failedLeadsPagination, setFailedLeadsPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        lastPage: 1,
    });

    const [duplicateLeadsPagination, setDuplicateLeadsPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        lastPage: 1,
    });
    const [activeTab, setActiveTab] = useState(0);
    const [isSaveMappingLoading, setIsSaveMappingLoading] = useState(false);
    const [isExportingMapping, setIsExportingMapping] = useState(false);

    const singleConnection = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection.singleConnection,
    );
    const singleConnectionMessage = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .singleConnectionMessage,
    );
    const singleConnectionErrorStatus = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .singleConnectionErrorStatus,
    );
    const activeConnectionId = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .activeConnectionId,
    );
    const isRefreshSourceLoading = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .isRefreshSourceLoading,
    );
    const shouldRefetchSourcePages = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .shouldRefetchSourcePages,
    );

    const allLeadDestinations = useSelector(
        (state) =>
            state.leadAllDestinationsState.getAllLeadDestinationTypes
                .leadDestinations,
    );

    const allLeadSources = useSelector(
        (state) => state.leadAllSourcesState.getAllLeadSourcesTypes.leadSources,
    );
    const bussiness = useSelector(
        (state) => state.allDashboardDatasState.getAllDashboardDatas.bussiness,
    );
    const appObject = useSelector((state) => state.setAppId.value);

    useEffect(() => {
        if (!appObject?.app_id) {
            return;
        }
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: appObject.app_id.toString(),
                autoLogAppEvents: true,
                xfbml: true,
                version: "v2.9",
            });
        };
        (function (d, s, id) {
            var js,
                fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) {
                return;
            }
            js = d.createElement(s);
            js.id = id;
            js.src = "https://connect.facebook.net/en_US/sdk.js";
            fjs.parentNode.insertBefore(js, fjs);
        })(document, "script", "facebook-jssdk");
    }, [appObject?.app_id]);

    useEffect(() => {
        // Only react to the outcome of the request for this page's connection;
        // never redirect because of a stale/dropped response for another id.
        if (!params.id || String(params.id) !== activeConnectionId) {
            return;
        }
        if (singleConnectionErrorStatus === 404) {
            navigate(routes.connection);
            return;
        }
        if (singleConnectionMessage === "Error") {
            navigate("/error-page", {
                state: {
                    errorType: "NOT_FOUND_CONTENT",
                },
            });
        }
    }, [
        singleConnectionMessage,
        singleConnectionErrorStatus,
        activeConnectionId,
        params.id,
    ]);

    useEffect(() => {
        const businessId =
            bussiness &&
            bussiness.length > 0 &&
            bussiness.find((element) => element.selected === true);
        if (businessId?.id) {
            setBusinessIdState(businessId.id);
        }
    }, [bussiness]);

    useEffect(() => {
        dispatch(getAllLeadDestinationTypesAsync());
        dispatch(getAllLeadSourcesTypesAsync());
        if (params.id) {
            handleGetFailedLeads();
            handleGetDuplicateLeads();
        }
        // Intentionally no clearState() on unmount: the connection detail is
        // kept in the redux cache so revisiting the page can render instantly.
    }, []);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const [functionsMaxLenValues, setFunctionsMaxLenValues] = useState({});
    const [functionsPadWithValues, setFunctionsPadWithValues] = useState({});
    const [functionsPadWithCharacter, setFunctionsPadWithCharacter] = useState(
        {},
    );

    const [functionsLeftValues, setFunctionsLeftValues] = useState({});
    const [functionsRightValues, setFunctionsRightValues] = useState({});

    // EDİT START
    const destinationOnChange = (e) => {
        dispatch(resetLastAddedService());
        setConnectWebServicesList([]);
        setDestinationSelections(e?.value);
        setSelectedWebService(null);
        setDestinationSelectionCode(e?.value);
        setSelectedZohoField("");
        if (e?.value) {
            getWebServices(e?.value);
        }
    };

    useEffect(() => {
        if (selectedZohoField.children) {
            setSelectedZohoFieldChildrenOptions(selectedZohoField.children);
        } else {
            setSelectedZohoFieldChildrenOptions([]);
        }
        setSelectedZohoFieldChildren("");
    }, [selectedZohoField]);

    useEffect(() => {
        if (selectedZohoFieldChildren.children) {
            setSelectedZohoFieldChildrentoChildrenOptions(
                selectedZohoFieldChildren.children,
            );
        } else {
            setSelectedZohoFieldChildrentoChildrenOptions([]);
        }
        setSelectedZohoFieldChildrenToChildren("");
    }, [selectedZohoFieldChildren]);

    const getWebServices = async (value) => {
        let response = await getServicesAsync(value);
        if (response.status) {
            setConnectWebServicesList(response?.data ?? "");
        }
    };

    const connectWebServicesOnChange = (e) => {
        if (e?.target?.value === null) {
            setSelectedWebService(null);
            return;
        }
        setSelectedWebService(e?.target?.value);
        setSelectedWebServiceId(e?.target?.value);
    };

    const zohoFieldOnChange = (id) => {
        setSelectedZohoField(id);
    };
    const zohoFieldChildrenOnChange = (id) => {
        setSelectedZohoFieldChildren(id);
    };
    const zohoFieldChildrenToChildrenOnChange = (id) => {
        setSelectedZohoFieldChildrenToChildren(id);
    };

    const handleEditDestination = async () => {
        let connectionId = singleConnection?.connection_detail?.id;
        if (defaultWebServiceId !== undefined && defaultWebServiceId !== null) {
            let response = await deleteDestinationWithConnection(
                connectionId,
                defaultWebServiceId,
            );
            if (response.status) {
                addDestination(connectionId);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } else {
            addDestination(connectionId);
        }
    };

    const addDestination = async (connectionId) => {
        let response = await addDestinationWithConnection(
            connectionId,
            selectedWebServiceId,
        );
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message && response.message,
                icon: "success",
                timer: 3000,
            });
            setDestinationPagesShow(false);
            dispatch(getSingleConnectionRedux(params.id && params.id));
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };
    const handleGetFailedLeads = useCallback(
        async (page = 1, pageSize = 10) => {
            setIsLoadingFailedLeads(true);
            const response = withDevMockLeads(
                await getFailedLeads(params.id, page, pageSize),
                () => buildDevMockFailedLeadsResponse(page, pageSize),
            );
            if (response.status) {
                setFailedLeads(response.data.leads);
                setFailedLeadsPagination({
                    currentPage: response.data.pagination.current_page,
                    pageSize: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                    lastPage: response.data.pagination.last_page,
                });
            }
            setIsLoadingFailedLeads(false);
        },
        [params, setFailedLeadsPagination, setFailedLeads],
    );

    const handleGetDuplicateLeads = useCallback(
        async (page = 1, pageSize = 10) => {
            setIsLoadingDuplicateLeads(true);
            const response = withDevMockLeads(
                await getDuplicateLeads(params.id, page, pageSize),
                () => buildDevMockDuplicateLeadsResponse(page, pageSize),
            );
            if (response.status) {
                setDuplicateLeads(response.data.leads);
                setDuplicateLeadsPagination({
                    currentPage: response.data.pagination.current_page,
                    pageSize: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                    lastPage: response.data.pagination.last_page,
                });
            }
            setIsLoadingDuplicateLeads(false);
        },
        [params, setDuplicateLeadsPagination, setDuplicateLeads],
    );

    // Keyed by issue so the row that opened the dialog can be refocused when it
    // closes, whichever row that was.
    const leadIssueRowRefs = useRef({});
    const lastOpenedLeadIssue = useRef(null);

    const openLeadIssues = useCallback((issueKey, tabIndex) => {
        lastOpenedLeadIssue.current = issueKey;
        setActiveTab(tabIndex);
        setIsFailedLeadsModalOpen(true);
    }, []);

    const leadIssues = useMemo(
        () =>
            [
                {
                    key: "undeliverable",
                    label:
                        language?.connections?.undeliverable_leads ??
                        "Undeliverable Leads",
                    count: failedLeadsPagination?.total || 0,
                    severity: "error",
                    icon: <MarkEmailUnreadOutlinedIcon fontSize="small" />,
                    tabIndex: 0,
                },
                {
                    key: "duplicate",
                    label:
                        language?.connections?.duplicate_leads ??
                        "Duplicate Leads",
                    count: duplicateLeadsPagination?.total || 0,
                    severity: "warning",
                    icon: <ContentCopyOutlinedIcon fontSize="small" />,
                    tabIndex: 1,
                },
            ].filter((issue) => issue.count > 0),
        [language, failedLeadsPagination, duplicateLeadsPagination],
    );

    const handleCloseLeadIssues = useCallback(() => {
        setIsFailedLeadsModalOpen(false);
        // MUI's Dialog already restores focus to the trigger; this lands on the
        // same node and covers the dialog being unmounted before it can.
        leadIssueRowRefs.current[lastOpenedLeadIssue.current]?.focus();
    }, []);
    useEffect(() => {
        if (editFunctionsOptions.length > 0) {
            setNewFunctionsOptions(
                editFunctionsOptions.map((item) => ({
                    id: item.code,
                    value: item.code,
                    label: item.name,
                    value_types: item.value_types,
                })),
            );
        }
    }, [editFunctionsOptions]);

    // EDİT START

    const isFacebookLeadSource =
        singleConnection?.source?.source_type_code === "fl";

    // Raw Meta type for a mapping row: `source_field.type` when the API nests
    // it, otherwise `source_field_type` (hydrated) or the row-level `type`.
    const getMappingSourceType = (field) => {
        if (!field) return "";
        const raw = field.source_field;
        if (raw && typeof raw === "object") {
            return raw.type?.toString?.() ?? "";
        }
        if (typeof field.source_field_type === "string") {
            return field.source_field_type;
        }
        if (
            typeof field.type === "string" &&
            field.type !== "basic_mode" &&
            field.type !== "advanced_mode"
        ) {
            return field.type;
        }
        return "";
    };

    // Facebook: identity is the Meta type token (PHONE, CREATED_UNIX_TIME, ...)
    // except for advertiser questions (type CUSTOM), which are identified by
    // the question key. Everything else: identity is the key.
    const getMappingSourceIdentity = (field) => {
        if (!field) return "";
        const isDynamic = !!field.isDynamic || !!field.is_dynamic;
        const raw = field.source_field;
        const key =
            raw && typeof raw === "object"
                ? (raw.key?.toString?.() ?? "")
                : (raw?.toString?.() ?? "");
        const explicitType = getMappingSourceType(field);
        if (isFacebookLeadSource && !isDynamic && explicitType) {
            return getFacebookSourceIdentity({ type: explicitType, key });
        }
        return key;
    };

    const sourceFieldOptions = fieldName
        .map((field) => {
            const raw = field.source_field;
            const key =
                raw && typeof raw === "object"
                    ? (raw.key?.toString?.() ?? "")
                    : (raw?.toString?.() ?? "");
            return {
                value: getMappingSourceIdentity(field),
                key,
                type: getMappingSourceType(field),
                label:
                    field.source_label || (typeof raw === "string" ? raw : key),
                isDynamic: !!field.isDynamic || !!field.is_dynamic,
            };
        })
        .filter(
            (item, index, self) =>
                index ===
                self.findIndex(
                    (t) => t.value === item.value && t.label === item.label,
                ),
        );

    const handleSourceFieldSelect = (selectedOption, index) => {
        const selectedField = selectedOption.target.value;
        const selectedFieldOption = sourceFieldOptions.find(
            (opt) => opt.value === selectedField,
        );

        setFieldMappingErrorIndexes((prev) => {
            const newIndexes = [...(prev?.notFilledSourceIndexes || [])];
            const toBeRemovedIndex = newIndexes.indexOf(index);
            if (toBeRemovedIndex > -1) {
                newIndexes.splice(toBeRemovedIndex, 1);
            }
            return { ...prev, notFilledSourceIndexes: newIndexes };
        });

        const updatedMatchFields = matchFields.map((item, i) =>
            i === index
                ? {
                      ...item,
                      source_field: selectedField,
                      source_field_key:
                          selectedFieldOption?.key || selectedField,
                      source_field_type:
                          selectedFieldOption?.type || selectedField,
                      source_label: selectedFieldOption?.label || selectedField,
                      is_dynamic: !!selectedFieldOption?.isDynamic,
                      dynamic_field_name: selectedFieldOption?.isDynamic
                          ? selectedField
                          : undefined,
                      options: { isDefOpen: false, isPrefixOpen: false },
                  }
                : item,
        );

        setMatchFields(updatedMatchFields);
    };

    const handleSourceFieldChange = (e, index) => {
        setFieldMappingErrorIndexes((prev) => {
            const toBeRemovedIndex =
                prev?.notFilledSourceIndexes?.indexOf(index);
            if (index > -1 && e.target.value) {
                prev?.notFilledSourceIndexes?.splice(toBeRemovedIndex, 1);
            } else if (index === -1 && !e.target.value) {
                prev?.notFilledSourceIndexes?.push(index);
            }
            return prev;
        });
        const newArray = matchFields.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    source_field: e.target.value,
                    options: { isDefOpen: false, isPrefixOpen: false },
                };
            }
            return item;
        });
        setMatchFields(newArray);
    };

    const handleDestinationFieldSelect = (selectedValue, index) => {
        const selectedFieldOption = newLeadDestination?.find(
            (opt) => opt.type === selectedValue,
        );

        setFieldMappingErrorIndexes((prev) => {
            const newIndexes = [...(prev?.notFilledDestinationIndexes || [])];
            const toBeRemovedIndex = newIndexes.indexOf(index);
            if (toBeRemovedIndex > -1) {
                newIndexes.splice(toBeRemovedIndex, 1);
            }
            return { ...prev, notFilledDestinationIndexes: newIndexes };
        });

        const newArray = matchFields.map((item, i) =>
            i === index
                ? {
                      ...item,
                      destination_field: selectedValue,
                      destination_label:
                          selectedFieldOption?.name || selectedValue,
                      options: { isDefOpen: false, isPrefixOpen: false },
                  }
                : item,
        );

        setMatchFields(newArray);
    };

    const [selectedFunctions, setSelectedFunctions] = useState({});

    const handleFieldFunctionsChange = (index, values) => {
        setSelectedFunctions((prev) => ({ ...prev, [index]: values }));
        setMatchFields((prev) => {
            const newList = [...prev];
            newList[index] = { ...newList[index], fn: values };
            return newList;
        });
    };

    const handleFunctionValueChange = (index, key, value) => {
        const setters = {
            maxLen: setFunctionsMaxLenValues,
            padWithLength: setFunctionsPadWithValues,
            padWithCharacter: setFunctionsPadWithCharacter,
            left: setFunctionsLeftValues,
            right: setFunctionsRightValues,
        };
        const setter = setters[key];
        if (setter) {
            setter((prev) => ({ ...prev, [index]: value }));
        }
    };

    const handleEditDynamicValue = (groupIndex) => {
        setIsDynamicValueOpen(true);
        setDynamicValueIndex(groupIndex);
    };

    const fetchDynamicFields = async () => {
        if (!params.id) {
            setIsDynamicFieldsLoading(false);
            return;
        }
        try {
            const response = await getDynamicFields(params.id);
            if (response.status) {
                const raw = response.data;
                const list = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data)
                      ? raw.data
                      : Array.isArray(raw?.dynamic_fields)
                        ? raw.dynamic_fields
                        : [];
                setDynamicFields(list);
            }
        } finally {
            setIsDynamicFieldsLoading(false);
        }
    };

    useEffect(() => {
        setIsDynamicFieldsLoading(true);
        fetchDynamicFields();
    }, [params.id]);

    const onDynamicVariableCreate = async (newVariable) => {
        const connectionId = singleConnection?.connection_detail?.id;
        const sourcDestMappingIds =
            singleConnection?.connection_detail
                ?.source_destination_mapping_ids ||
            singleConnection?.template?.field_mappings?.map((fm) => fm.id) ||
            [];

        const dynamicFieldsPayload = {
            source_destination_mappings: sourcDestMappingIds,
            dynamic_fields: {
                basic_mode:
                    newVariable.mode === "basic"
                        ? [
                              {
                                  name: newVariable.name,
                                  payload: (newVariable.fields || []).map(
                                      (f) => ({
                                          key: f.value,
                                          default: f.defaultValue || null,
                                          prefix: f.prefix || null,
                                          suffix: f.suffix || null,
                                          fn: (f.fn || []).map((fnItem) =>
                                              typeof fnItem === "string"
                                                  ? {
                                                        fn_name: fnItem,
                                                        fn_values: {},
                                                    }
                                                  : fnItem,
                                          ),
                                      }),
                                  ),
                              },
                          ]
                        : [],
                advanced_mode:
                    newVariable.mode === "advanced"
                        ? [
                              {
                                  name: newVariable.name,
                                  payload: newVariable.payload,
                              },
                          ]
                        : [],
            },
        };

        await saveDynamicFields(dynamicFieldsPayload);
        fetchDynamicFields();

        const alreadyExists = fieldName.find(
            (f) => f.source_field === newVariable.name,
        );
        if (!alreadyExists) {
            setFieldName((prev) => [
                ...prev,
                {
                    source_field: newVariable.name,
                    source_label: newVariable.name,
                    isDynamic: true,
                    fn: [],
                    default: "",
                    is_static: false,
                    destination_field: null,
                },
            ]);
        }

        setMatchFields((prev) => [
            ...prev,
            {
                source_field: newVariable.name,
                source_label: newVariable.name,
                destination_field: "",
                destination_label: "",
                fn: [],
                prefix: "",
                suffix: "",
                default: "",
                is_static: false,
                is_dynamic: true,
                dynamic_field_name: newVariable.name,
                options: { isDefOpen: false, isPrefixOpen: false },
            },
        ]);

        setIsDynamicValueOpen(false);
    };
    useEffect(() => {
        getSourceAndDestinationData();
    }, []);

    const getSourceAndDestinationData = async () => {
        let response = await getFunctions();
        if (response.status) {
            setEditFunctionsOptions(response.data?.functions);
            setEditFilterOptions(response.data?.filters);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    useEffect(() => {
        if (editFunctionsOptions.length > 0) {
            setNewFunctionsOptions(
                editFunctionsOptions.map((item) => ({
                    id: item.code,
                    value: item.code,
                    label: item.name,
                    value_types: item.value_types,
                })),
            );
        }
    }, [editFunctionsOptions]);

    useEffect(() => {
        const pagesCount = singleConnection?.source?.pages?.length || 0;

        if (pagesCount <= 3) {
            setIsSourcePopoverVisible(false);
        }
    }, [singleConnection?.source?.pages]);

    const loading = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection
                .singleConnectionStatus,
    );

    const isSourceExpired = useMemo(
        () => singleConnection?.source?.pages?.some((page) => !page?.status),
        [singleConnection],
    );
    const sourcePagesCount = singleConnection?.source?.pages?.length || 0;
    const sourceFormsCount =
        singleConnection?.source?.pages?.flatMap((page) => page.forms)
            ?.length || 0;
    const shouldShowSourceExpand = sourceFormsCount > 3 || sourcePagesCount > 3;
    const currentSourceTypeName = getSourceName(
        singleConnection?.source?.source_type,
    );
    const isTiktokSource = currentSourceTypeName === "Tiktok Lead Generation";
    const isFacebookSource = currentSourceTypeName === "Facebook Lead";
    const isLinkedinSource =
        currentSourceTypeName === "Linkedin Lead Generation";

    const renderSourceExpandButton = () =>
        shouldShowSourceExpand ? (
            <Box className="connection-detail-expand-button connection-detail-expand-button--inline">
                <StandartButton
                    className="tour-source-expand-btn"
                    variant={ButtonVariant.ICON_BUTTON}
                    rounded
                    setRef={setSourceCardRef}
                    onClick={() =>
                        setIsSourcePopoverVisible(!isSourcePopoverVisible)
                    }
                >
                    {isSourcePopoverVisible ? (
                        <KeyboardArrowUp />
                    ) : (
                        <KeyboardArrowDownIcon />
                    )}
                </StandartButton>
            </Box>
        ) : null;
    const isDestinationExpired = useMemo(
        () => !singleConnection?.destination?.status && loading !== "loading",
        [singleConnection, loading],
    );

    useEffect(() => {
        if (!params.id) {
            return undefined;
        }
        // Stale-while-revalidate: show the cached connection (if any) right
        // away, then always refetch. Abort the in-flight request when the id
        // changes or the page unmounts so a late response can't overwrite the
        // store with another connection's data.
        dispatch(hydrateSingleConnectionFromCache(params.id));
        const request = dispatch(getSingleConnectionRedux(params.id));
        return () => request.abort();
    }, [params.id]);

    useEffect(() => {
        if (singleConnection) {
            if (
                singleConnection?.template?.field_mappings?.[0]?.filters
                    ?.comparings === undefined
            ) {
                setConnectionFilters([]);
            } else {
                setConnectionFilters(
                    singleConnection?.template?.field_mappings?.[0]?.filters
                        ?.comparings,
                );
            }
            setSelectedOperator(
                singleConnection?.template?.field_mappings?.[0]?.filters
                    ?.operator,
            );
        }
    }, [singleConnection]);

    useEffect(() => {
        setSourceTypeName(singleConnection?.source?.source_type ?? "");
        setSourcesDetails(
            singleConnection &&
                singleConnection?.source &&
                singleConnection?.source?.details,
        );
        setFieldDestId({
            connection_id: singleConnection?.connection_detail?.id,
            destination_id: singleConnection?.destination?.id,
        });

        if (
            singleConnection &&
            singleConnection?.source &&
            singleConnection?.source?.pages
        ) {
            if (singleConnection?.source?.source_type_code !== "tiktok") {
                let pages = Object.values(singleConnection?.source?.pages);
                let pageNames =
                    pages.length > 0 && pages?.map((item) => item.name);
                let forms =
                    pages.length > 0 && pages?.map((item) => item.forms);
                let formNames =
                    forms &&
                    forms.length > 0 &&
                    forms.flat().map((form) => form.name);
                setFormNames(formNames);
                setPageNames(pageNames);
            } else {
                let pages = Object.values(singleConnection?.source?.pages);
                let pageNames =
                    pages.length > 0 && pages?.map((item) => item.name);
                setFormNames(pageNames);
            }
        }

        setDestinationPageName(singleConnection?.destination?.name ?? "");
        setDestinationType(singleConnection?.destination?.type ?? "");

        if (
            singleConnection &&
            singleConnection.template &&
            singleConnection.template.field_mappings
        ) {
            let fieldsObj = [];

            singleConnection?.template?.field_mappings.forEach(
                (mappingItem) => {
                    if (mappingItem?.mappings) {
                        fieldsObj.push(...mappingItem?.mappings);
                        mappingItem?.mappings.forEach((item, index) => {});
                    }
                },
            );
            if (fieldsObj) {
                if (fieldsObj.length > 0) {
                    let _matchFields = fieldsObj
                        .filter(
                            (item) =>
                                item.destination_field !== null &&
                                item.source_field !== null,
                        )
                        .map((item, index) => {
                            const raw = item.source_field;
                            const originalKey =
                                raw && typeof raw === "object"
                                    ? (raw.key?.toString?.() ?? "")
                                    : raw === "-"
                                      ? ""
                                      : (raw?.toString?.() ?? "");
                            return {
                                ...item,
                                source_field:
                                    raw === "-"
                                        ? ""
                                        : getMappingSourceIdentity(item),
                                source_field_key: originalKey,
                                source_field_type:
                                    raw === "-"
                                        ? ""
                                        : getMappingSourceType(item),
                                options: {
                                    isDefOpen: false,
                                    isPrefixOpen: false,
                                },
                                originalIndex: index, // Store original index for function value mapping
                            };
                        });
                    let _selectedFunctions = {};
                    let _functionsMaxLenValues = {};
                    let _functionsPadWithValues = {};
                    let _functionsPadWithCharacter = {};
                    let _functionsLeftValues = {};
                    let _functionsRightValues = {};

                    // Process function values first (before grouping)
                    _matchFields.forEach((field, index) => {
                        if (Array.isArray(field?.fn)) {
                            _selectedFunctions[index] = field?.fn.map(
                                (x) => x?.fn_name,
                            );
                            field?.fn.forEach((fn) => {
                                if (fn?.fn_name === "max_len") {
                                    if (fn?.fn_values?.length) {
                                        _functionsMaxLenValues[index] =
                                            fn?.fn_values?.length;
                                    }
                                } else if (fn?.fn_name === "pad_with") {
                                    if (fn?.fn_values?.length) {
                                        _functionsPadWithValues[index] =
                                            fn?.fn_values?.length;
                                        _functionsPadWithCharacter[index] =
                                            fn?.fn_values?.character;
                                    }
                                } else if (fn?.fn_name === "left") {
                                    if (fn?.fn_values?.length) {
                                        _functionsLeftValues[index] =
                                            fn?.fn_values?.length;
                                    }
                                } else if (fn?.fn_name === "right") {
                                    if (fn?.fn_values?.length) {
                                        _functionsRightValues[index] =
                                            fn?.fn_values?.length;
                                    }
                                }
                            });
                        } else {
                            _selectedFunctions[index] = [];
                        }
                    });

                    // Process compared fields
                    const comparedFields =
                        singleConnection.template.field_mappings[0]
                            ?.compared_fields?.[0];
                    if (
                        !!comparedFields &&
                        !!Object.keys(comparedFields)?.length
                    ) {
                        const comparedFieldsKeys = Object.keys(comparedFields);
                        _matchFields = _matchFields?.map((field) => {
                            field.compared_fields = {};
                            const indexOfComparedFieldKeys =
                                comparedFieldsKeys.findIndex(
                                    (key) =>
                                        key === field?.source_field ||
                                        key === field?.source_field_key,
                                );
                            if (indexOfComparedFieldKeys > -1) {
                                const key =
                                    comparedFieldsKeys[
                                        indexOfComparedFieldKeys
                                    ];
                                const storeKey = field.source_field || key;
                                field.compared_fields[storeKey] =
                                    comparedFields[key];
                            }
                            return field;
                        });
                    }

                    setFunctionsMaxLenValues(_functionsMaxLenValues);
                    setFunctionsPadWithValues(_functionsPadWithValues);
                    setFunctionsPadWithCharacter(_functionsPadWithCharacter);
                    setSelectedFunctions(_selectedFunctions);
                    setFunctionsLeftValues(_functionsLeftValues);
                    setFunctionsRightValues(_functionsRightValues);
                    setMatchFields(_matchFields);
                }
                // Add dynamic field names as source field options
                singleConnection?.template?.field_mappings?.forEach(
                    (mappingItem) => {
                        if (mappingItem?.dynamic_fields) {
                            if (
                                Array.isArray(
                                    mappingItem.dynamic_fields.basic_mode,
                                )
                            ) {
                                mappingItem.dynamic_fields.basic_mode.forEach(
                                    (dynField) => {
                                        const exists = fieldsObj.find(
                                            (f) =>
                                                f.source_field ===
                                                dynField.name,
                                        );
                                        if (!exists) {
                                            fieldsObj.push({
                                                source_field: dynField.name,
                                                source_label: dynField.name,
                                                isDynamic: true,
                                                fn: [],
                                                default: "",
                                                is_static: false,
                                                destination_field: null,
                                            });
                                        }
                                    },
                                );
                            }
                            if (
                                Array.isArray(
                                    mappingItem.dynamic_fields.advanced_mode,
                                )
                            ) {
                                mappingItem.dynamic_fields.advanced_mode.forEach(
                                    (dynField) => {
                                        const exists = fieldsObj.find(
                                            (f) =>
                                                f.source_field ===
                                                dynField.name,
                                        );
                                        if (!exists) {
                                            fieldsObj.push({
                                                source_field: dynField.name,
                                                source_label: dynField.name,
                                                isDynamic: true,
                                                fn: [],
                                                default: "",
                                                is_static: false,
                                                destination_field: null,
                                            });
                                        }
                                    },
                                );
                            }
                        }
                    },
                );
                setFieldName(fieldsObj);
            }
            if (singleConnection?.destination) {
                setSelectedWebService(singleConnection?.destination?.name);
                setSelectedWebServiceId(singleConnection?.destination?.id);
                setDefaultWebServiceId(singleConnection?.destination?.id);
                setDestinationSelections(singleConnection?.destination?.type);
                if (allLeadDestinations) {
                    let code = allLeadDestinations.find(
                        (x) => x.name === singleConnection?.destination?.type,
                    )?.code;
                    if (code) {
                        getWebServices(code);
                    }
                }
            }
        }
    }, [singleConnection]);
    const handleFieldRemove = async (index) => {
        setFieldMappingErrorIndexes((prev) => {
            const toBeRemovedSourceIndex =
                prev?.notFilledSourceIndexes?.indexOf(index);
            if (index > -1) {
                prev?.notFilledSourceIndexes?.splice(toBeRemovedSourceIndex, 1);
            } else if (index === -1) {
                prev?.notFilledSourceIndexes?.push(index);
            }

            const toBeRemovedDestinationIndex =
                prev?.notFilledSourceIndexes?.indexOf(index);
            if (index > -1) {
                prev?.notFilledDestinationIndexes?.splice(
                    toBeRemovedDestinationIndex,
                    1,
                );
            } else if (index === -1) {
                prev?.notFilledDestinationIndexes?.push(index);
            }
            return prev;
        });

        setSelectedFunctions({
            ...selectedFunctions,
            [index]: [],
        });

        const list = [...matchFields];
        list.splice(index, 1);
        setMatchFields(list);
    };

    const handleFieldAdd = () => {
        setMatchFields([
            ...matchFields,
            {
                destination_field: "",
                fn: [],
                source_field: "",
                default: "",
                prefix: "",
                options: { isDefOpen: false, isPrefixOpen: false },
            },
        ]);
    };

    const handleStaticFieldAdd = () => {
        setMatchFields([
            ...matchFields,
            {
                default: "",
                destination_field: "",
                fn: [],
                source_field: "",
                is_static: true,
                options: { isDefOpen: false, isPrefixOpen: false },
            },
        ]);
    };

    const getLeadDestinationsCall = async () => {
        let response = await getLeadDestinationId(fieldDestId);
        setNewLeadDestination(response?.data);
    };

    const badgeCreator = (item) => {
        let fn = item.fn?.map((x) => x?.fn_name);
        if (!fn) return 0;
        if (item?.default !== null && item?.default !== "") {
            fn.push(item?.default);
        }
        if (item?.prefix !== null && item?.prefix !== "") {
            fn.push(item?.prefix);
        }
        if (
            item?.compared_fields &&
            !!Object.keys(item?.compared_fields)?.length
        )
            fn.push(item?.compared_fields);
        if (Array.isArray(fn)) {
            return fn.length;
        } else {
            return 0;
        }
    };

    const badgeCreatorForDynamicGroup = (item) => {
        if (!item?.isDynamicGroup || !item?.fields) return 0;
        let totalCount = 0;
        item.fields.forEach((field) => {
            if (field?.fn && Array.isArray(field.fn) && field.fn.length > 0) {
                totalCount += field.fn.length;
            }
            if (field?.default !== null && field?.default !== "") {
                totalCount += 1;
            }
            if (field?.prefix !== null && field?.prefix !== "") {
                totalCount += 1;
            }
        });
        return totalCount;
    };

    const handleGetDocumentButton = async () => {
        if (sourceTypeName === "Source API") {
        }
        let response = await getSourceApiDocument(sourcesDetails?.id);
        if (response.status) {
            const url = response?.data?.url ?? "";
            const newTab = window.open(url, "_blank");
            newTab.focus();
        }
    };

    // Destination types that support field reload from CRM (Phase 1)
    const RELOAD_SUPPORTED_DESTINATIONS = [
        "Bitrix 24",
        "Salesforce",
        "Microsoft Dynamics",
        "microsoft-dynamics",
        "Hubspot",
    ];

    const handleClickFieldEditBtn = async () => {
        if (fieldEditPermission) {
            setFieldEditPermission(false);
            return;
        }

        dynamicFieldDeletedDuringEditRef.current = false;
        mappingEditSnapshotRef.current = {
            matchFields: JSON.parse(JSON.stringify(matchFields)),
            selectedFunctions: JSON.parse(JSON.stringify(selectedFunctions)),
            functionsMaxLenValues: JSON.parse(
                JSON.stringify(functionsMaxLenValues),
            ),
            functionsPadWithValues: JSON.parse(
                JSON.stringify(functionsPadWithValues),
            ),
            functionsPadWithCharacter: JSON.parse(
                JSON.stringify(functionsPadWithCharacter),
            ),
            functionsLeftValues: JSON.parse(
                JSON.stringify(functionsLeftValues),
            ),
            functionsRightValues: JSON.parse(
                JSON.stringify(functionsRightValues),
            ),
        };

        const shouldReloadFields =
            RELOAD_SUPPORTED_DESTINATIONS.includes(destinationType);

        setIsReloadingFields(true);
        try {
            // First reload fields from CRM to get latest updates (only for supported destinations)
            if (shouldReloadFields) {
                await reloadDestinationFields(fieldDestId);
            }
            // Then fetch the updated fields
            await getLeadDestinationsCall();
        } catch (error) {
            console.error("Error reloading fields:", error);
        } finally {
            setIsReloadingFields(false);
        }
        setFieldEditPermission(true);
    };

    const handleClickCancelBtn = () => {
        if (dynamicFieldDeletedDuringEditRef.current) {
            dynamicFieldDeletedDuringEditRef.current = false;
            mappingEditSnapshotRef.current = null;
            dispatch(getSingleConnectionRedux(params.id && params.id));
            fetchDynamicFields();
        } else {
            const snapshot = mappingEditSnapshotRef.current;
            if (snapshot) {
                setMatchFields(snapshot.matchFields);
                setSelectedFunctions(snapshot.selectedFunctions);
                setFunctionsMaxLenValues(snapshot.functionsMaxLenValues);
                setFunctionsPadWithValues(snapshot.functionsPadWithValues);
                setFunctionsPadWithCharacter(
                    snapshot.functionsPadWithCharacter,
                );
                setFunctionsLeftValues(snapshot.functionsLeftValues);
                setFunctionsRightValues(snapshot.functionsRightValues);
            }
        }
        setFieldMappingErrorIndexes({});
        setFieldEditPermission(false);
    };

    const [fieldMappingErrorIndexes, setFieldMappingErrorIndexes] = useState(
        {},
    );

    const handleRefreshSource = async () => {
        dispatch(refreshSource(singleConnection?.source?.account_id));
    };

    const handleExportMapping = async () => {
        const connectionId = singleConnection?.connection_detail?.id;
        const fieldMappingId =
            singleConnection?.template?.field_mappings?.[0]?.id;
        if (!connectionId || !fieldMappingId) {
            return;
        }
        setIsExportingMapping(true);
        const response = await exportFieldMapping(connectionId, fieldMappingId);
        setIsExportingMapping(false);
        if (!response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const handleFielSaveChanges = async () => {
        setIsSaveMappingLoading(true);
        let connectionId = singleConnection?.connection_detail?.id;
        let fieldMappingId = singleConnection?.template?.field_mappings[0]?.id;
        let obj = {
            field_mapping: [],
        };

        const flattenedFields = [...matchFields];

        // Facebook: `type` is always the Meta enum (PHONE, EMAIL, ..., CUSTOM,
        // CREATED_UNIX_TIME) and `key` is the question / locale key. The UI
        // row keeps the identity in `source_field`; recover the raw parts from
        // the row (hydrated) or from the matching source option.
        const buildFacebookSourceField = (row) => {
            const identity = row?.source_field?.toString?.() ?? "";
            if (row?.is_static || row?.is_dynamic) {
                return { type: identity, key: identity };
            }
            const option = sourceFieldOptions.find(
                (opt) => !opt.isDynamic && opt.value === identity,
            );
            const type = row?.source_field_type || option?.type || identity;
            const key = row?.source_field_key || option?.key || identity;
            return { type: type.toString(), key: key.toString() };
        };

        const savedSourceIdentity = (savedSource) =>
            isFacebookLeadSource
                ? getFacebookSourceIdentity(savedSource)
                : (savedSource?.key?.toString?.() ?? "");

        Object.keys(flattenedFields).forEach((index) => {
            let item = flattenedFields[index];
            const rowIdentity = item.source_field?.toString?.() ?? "";
            if (
                obj.field_mapping.find(
                    (x) => savedSourceIdentity(x.source_field) === rowIdentity,
                ) === undefined
            ) {
                obj.field_mapping.push({
                    source_field: isFacebookLeadSource
                        ? buildFacebookSourceField(item)
                        : {
                              type: item.source_field,
                              key: item.source_field_key || item.source_field,
                          },
                    mappings: [],
                });
            }
        });
        obj.field_mapping.forEach((item) => {
            flattenedFields.forEach((field, index) => {
                const fnValueIndex =
                    field?.originalIndex !== undefined
                        ? field.originalIndex
                        : index;

                const rowMatchesSource =
                    (field.source_field?.toString?.() ?? "") ===
                    savedSourceIdentity(item.source_field);
                if (
                    rowMatchesSource ||
                    item.mappings.find(
                        (y) => y.destination_field === field.destination_field,
                    ) !== undefined
                ) {
                    item.mappings.push({
                        destination_field: field.destination_field?.toString(),
                        prefix: field.prefix,
                        suffix: field.suffix,
                        default: field?.is_static
                            ? field?.default?.toString() || field?.source_field
                            : field?.default?.toString() || "",
                        is_static: !!field?.is_static,
                        is_dynamic: !!field?.is_dynamic,
                        dynamic_field_name:
                            field?.dynamic_field_name || undefined,
                        fn: (field.fn || [])
                            .filter((x) => x !== null)
                            .map((fn) => {
                                if (fn) {
                                    if (fn === "max_len") {
                                        return {
                                            fn_name: fn,
                                            fn_values: {
                                                ["length"]: parseInt(
                                                    functionsMaxLenValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                            },
                                        };
                                    } else if (fn === "pad_with") {
                                        return {
                                            fn_name: fn,
                                            fn_values: {
                                                ["length"]: parseInt(
                                                    functionsPadWithValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                                ["character"]:
                                                    functionsPadWithCharacter[
                                                        fnValueIndex
                                                    ],
                                            },
                                        };
                                    } else if (fn?.fn_name === "max_len") {
                                        return {
                                            fn_name: fn.fn_name,
                                            fn_values: {
                                                ["length"]: parseInt(
                                                    functionsMaxLenValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                            },
                                        };
                                    } else if (fn?.fn_name === "pad_with") {
                                        return {
                                            fn_name: fn.fn_name,
                                            fn_values: {
                                                ["length"]: parseInt(
                                                    functionsPadWithValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                                ["character"]:
                                                    functionsPadWithCharacter[
                                                        fnValueIndex
                                                    ],
                                            },
                                        };
                                    } else if (fn === "left") {
                                        return {
                                            fn_name: fn,
                                            fn_values: {
                                                length: parseInt(
                                                    functionsLeftValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                            },
                                        };
                                    } else if (fn === "right") {
                                        return {
                                            fn_name: fn,
                                            fn_values: {
                                                length: parseInt(
                                                    functionsRightValues[
                                                        fnValueIndex
                                                    ],
                                                ),
                                            },
                                        };
                                    } else if (fn?.fn_name !== undefined) {
                                        return {
                                            fn_name: fn.fn_name,
                                            fn_values: {},
                                        };
                                    } else {
                                        return {
                                            fn_name: fn,
                                            fn_values: {},
                                        };
                                    }
                                }
                                return null;
                            })
                            .filter((x) => x !== null),
                    });
                }
            });
        });
        obj.field_mapping.forEach((item) => {
            const source_field = {
                key: "-",
                type: "-",
            };
            if (item && item.source_field && item.source_field.key === "") {
                item.source_field = source_field;
            }
        });
        const notFilledSourceIndexes = [];
        const notFilledDestinationIndexes = [];
        matchFields?.forEach((item, index) => {
            if (item?.source_field === "" && !item?.is_static)
                notFilledSourceIndexes.push(index);
            if (item?.destination_field === "")
                notFilledDestinationIndexes.push(index);
        });

        if (
            !!notFilledSourceIndexes.length ||
            !!notFilledDestinationIndexes.length
        ) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "Please fill required fields.",
                icon: "error",
            });
            setFieldMappingErrorIndexes({
                notFilledSourceIndexes,
                notFilledDestinationIndexes,
            });
            setIsSaveMappingLoading(false);
            return;
        }
        const comparedFields = {};
        matchFields.forEach((item) => {
            if (!item?.compared_fields) return;

            const comparisationKey = Object.keys(item?.compared_fields)[0];
            if (!!item?.compared_fields[comparisationKey]?.length) {
                comparedFields[comparisationKey] =
                    item?.compared_fields[comparisationKey];
            }
        });

        await updateConnectionComparedFields(connectionId, fieldMappingId, {
            compared_fields: !!Object.keys(comparedFields)?.length
                ? [comparedFields]
                : null,
        });

        let response = await updateNewConnectionField(
            connectionId,
            fieldMappingId,
            obj,
        );
        dispatch(getSingleConnectionRedux(params.id && params.id));
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message && response.message,
                icon: "success",
                timer: 3000,
            });
            setFieldEditPermission(false);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
        setIsSaveMappingLoading(false);
    };
    const [connectionSourceList, setConnectionSourceList] = useState([]);
    const [selectedConnectionSource, setSelectedConnectionSource] = useState();
    const [selectedConnectionSourceItem, setSelectedConnectionSourceItem] =
        useState(null);
    const [selectedConnectionPage, setSelectedConnectionPage] = useState([]);
    const [selectedConnectionForm, setSelectedConnectionForm] = useState([]);
    const [selectedConnectionSourceType, setSelectedConnectionSourceType] =
        useState("");
    const [addNewFormsValue, setAddNewFormsValue] = useState(null);

    const mapFbPagesToConnectionShape = (pages) =>
        (Array.isArray(pages) ? pages : []).map((page) => ({
            id: page.id,
            name: page.page_name ?? page.name,
            forms: (page.page_forms || page.forms || []).map((form) => ({
                id: form.id,
                name: form.name,
                parentId: page.id,
                ...(form.is_selected !== undefined
                    ? { is_selected: form.is_selected }
                    : {}),
            })),
            ...(page.is_selected !== undefined
                ? { is_selected: page.is_selected }
                : {}),
        }));

    const handleAddPageClick = () => {
        setIsAddPageLoading(true);
        if (DEV_MODE) {
            setTimeout(() => {
                handleAddPageWithToken(DEV_MOCK_FB_ACCESS_TOKEN);
            }, 800);
            return;
        }
        window.FB.login(
            function (responseFB) {
                if (responseFB.authResponse) {
                    handleAddPageWithToken(responseFB.authResponse.accessToken);
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message:
                            "Error. No Pixel found! User cancelled login or did not fully authorize.",
                        icon: "error",
                    });
                    setIsAddPageLoading(false);
                }
            },
            {
                scope: "pages_manage_ads,pages_read_engagement,pages_show_list,pages_manage_metadata,leads_retrieval,pages_show_list,pages_manage_metadata",
                auth_type: "rerequest",
            },
        );
    };

    const handleAddPageWithToken = async (accessToken) => {
        if (!accessToken) {
            setIsAddPageLoading(false);
            return;
        }
        setIsAddPageLoading(true);
        const response = await sendFBInfosToServisAsync({
            access_token: accessToken,
            business_id: businessIdState,
            source_type_code: "fl",
        });
        if (response.status) {
            const mappedPages = mapFbPagesToConnectionShape(response?.data);
            const validPageIds = new Set(mappedPages.map((page) => page.id));
            const validFormIds = new Set(
                mappedPages.flatMap((page) =>
                    Array.isArray(page.forms)
                        ? page.forms.map((form) => form.id)
                        : [],
                ),
            );
            setSelectedConnectionSourceItem((prev) => ({
                ...(prev || {}),
                pages: mappedPages,
            }));
            setSelectedConnectionPage((prev) =>
                (Array.isArray(prev) ? prev : []).filter((id) =>
                    validPageIds.has(id),
                ),
            );
            setSelectedConnectionForm((prev) =>
                (Array.isArray(prev) ? prev : []).filter((id) =>
                    validFormIds.has(id),
                ),
            );
            setIsAddPageLoading(false);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    (response.message || "") +
                    `${
                        language?.lead?.please_reload_page ??
                        "Please reload page"
                    }`,
                icon: "error",
            });
            setIsAddPageLoading(false);
        }
    };

    const handleOpenSourcePages = async () => {
        if (sourcePagesShow === false || shouldRefetchSourcePages) {
            setIsSourceEditLoading(true);
            let response = await getSourcesByConnectionId(
                params.id && params.id,
            );
            setIsSourceEditLoading(false);
            if (response.status) {
                setSourcePagesShow(true);
                setConnectionSourceList(response?.data);

                // TODO: this needs helper with handleChangeSource
                // TODO: on page change forms need to reorgnised
                const item = response?.data?.find(
                    (source) =>
                        source?.source_type ===
                            singleConnection?.source?.source_type_code &&
                        source?.id === singleConnection?.source?.account_id,
                );
                setSelectedConnectionSourceItem(item);
                setSelectedConnectionSource(item?.id);
                setSelectedConnectionSourceType(item?.source_type);
                if (item?.source_type === "tiktok") {
                    setSelectedConnectionPage(
                        Array.isArray(item?.pages)
                            ? item.pages
                                  .filter((x) => x.is_selected === true)
                                  .map((x) => x.id)
                            : [],
                    );
                } else if (item?.source_type === "fl") {
                    setSelectedConnectionPage(
                        Array.isArray(item?.pages)
                            ? item.pages
                                  .filter((x) => x.is_selected === true)
                                  .map((x) => x.id)
                            : [],
                    );
                    setSelectedConnectionForm(
                        Array.isArray(item?.pages)
                            ? item.pages.flatMap((page) =>
                                  Array.isArray(page?.forms)
                                      ? page.forms
                                            .filter(
                                                (x) => x.is_selected === true,
                                            )
                                            .map((form) => form.id)
                                      : [],
                              )
                            : [],
                    );
                }
                setAddNewFormsValue(
                    singleConnection?.connection_detail?.check_new_forms,
                ); //todo
                dispatch(clearRefetchSourcePages());
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
                setSourcePagesShow(false);
            }
        } else {
            setSourcePagesShow(false);
        }
    };

    useEffect(() => {
        shouldRefetchSourcePages && handleOpenSourcePages();
    }, [shouldRefetchSourcePages]);

    const handleChangeSource = (e) => {
        const item = connectionSourceList.find(
            (x) =>
                x.id === e?.target?.value &&
                x.source_type === selectedConnectionSourceType,
        );
        setSelectedConnectionSourceItem(item);
        setSelectedConnectionSource(e?.target?.value);
        if (item?.source_type === "tiktok") {
            setSelectedConnectionPage(
                Array.isArray(item?.pages)
                    ? item.pages
                          .filter((x) => x.is_selected === true)
                          .map((x) => x.id)
                    : [],
            );
        } else if (item?.source_type === "fl") {
            setSelectedConnectionPage(
                Array.isArray(item?.pages)
                    ? item.pages
                          .filter((x) => x.is_selected === true)
                          .map((x) => x.id)
                    : [],
            );
            setSelectedConnectionForm(
                Array.isArray(item?.pages)
                    ? item.pages.flatMap((page) =>
                          Array.isArray(page?.forms)
                              ? page.forms
                                    .filter((x) => x.is_selected === true)
                                    .map((form) => form.id)
                              : [],
                      )
                    : [],
            );
        }
    };
    const handleChangePage = (value) => {
        setSelectedConnectionPage(
            typeof value === "string" ? value.split(",") : value,
        );
        if (value?.length > 0 && typeof value !== "string") {
            setSelectedConnectionPage(value);
        } else {
            setSelectedConnectionPage([]);
        }
    };

    const [isSourceSaveLoading, setIsSourceSaveLoading] = useState(false);
    const [isSourceEditLoading, setIsSourceEditLoading] = useState(false);
    const [isTiktokRefreshLoading, setIsTiktokRefreshLoading] = useState(false);

    const handleRefreshTiktokForms = async () => {
        const sourceAccountId = selectedConnectionSourceItem?.id;
        if (!sourceAccountId) {
            return;
        }

        setIsTiktokRefreshLoading(true);
        const response = await reloadTiktokPages(sourceAccountId);
        setIsTiktokRefreshLoading(false);

        if (response.status) {
            const refreshedPages = Array.isArray(response?.data?.pages)
                ? response.data.pages
                : [];

            setSelectedConnectionSourceItem((prev) =>
                prev?.id === sourceAccountId && prev?.source_type === "tiktok"
                    ? { ...prev, pages: refreshedPages }
                    : prev,
            );
            setConnectionSourceList((prev) =>
                (Array.isArray(prev) ? prev : []).map((source) =>
                    source.id === sourceAccountId &&
                    source.source_type === "tiktok"
                        ? { ...source, pages: refreshedPages }
                        : source,
                ),
            );
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: "TikTok forms refreshed successfully.",
                icon: "success",
                timer: 3000,
            });
            return;
        }

        SwalAlertComp({
            title: language?.delete_swal_modal?.failed ?? "Failed",
            message: response.message ?? "Something went wrong!",
            icon: "error",
        });
    };

    const handleSaveSourceEdit = async () => {
        setIsSourceSaveLoading(true);
        let data = {};
        if (selectedConnectionSourceItem?.source_type === "fl") {
            if (selectedConnectionForm.length > 0) {
                data = {
                    type: selectedConnectionSourceItem?.source_type,
                    forms: getSelectableForms(selectedConnectionForm),
                    fb_pages: selectedConnectionPage,
                    source_id: selectedConnectionSourceItem?.id,
                    check_new_form: !!addNewFormsValue,
                };
            } else {
                setIsSourceSaveLoading(false);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        language?.delete_swal_modal?.please_select_forms ??
                        "Please select forms!",
                    icon: "error",
                });
                return;
            }
        } else if (selectedConnectionSourceItem?.source_type === "tiktok") {
            if (selectedConnectionPage.length > 0) {
                data = {
                    type: selectedConnectionSourceItem?.source_type,
                    pages: selectedConnectionPage,
                    source_id: selectedConnectionSourceItem?.id,
                    check_new_form: !!addNewFormsValue,
                };
            } else {
                setIsSourceSaveLoading(false);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        language?.delete_swal_modal?.please_select_forms ??
                        "Please select forms!",
                    icon: "error",
                });
                return;
            }
        } else if (selectedConnectionSourceItem?.source_type === "gglw") {
            data = {
                type: selectedConnectionSourceItem?.source_type,
                source_id: selectedConnectionSourceItem?.id,
            };
        } else {
            data = {
                type: selectedConnectionSourceItem?.source_type,
                source_api_id: selectedConnectionSourceItem?.id,
            };
        }
        let response = await editSourceByConnectionId(
            params.id && params.id,
            data,
        );
        setIsSourceSaveLoading(false);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message && response.message,
                icon: "success",
                timer: 3000,
            });
            setSourcePagesShow(false);
            dispatch(getSingleConnectionRedux(params.id && params.id));
        } else {
            setIsSourceSaveLoading(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const [connectionDestinationList, setConnectionDestinationList] = useState(
        [],
    );
    const [
        selectedConnectionDestinationType,
        setSelectedConnectionDestinationType,
    ] = useState("");
    const [selectedConnectionDestination, setSelectedConnectionDestination] =
        useState("");
    const [
        selectedConnectionDestinationItem,
        setSelectedConnectionDestinationItem,
    ] = useState(null);
    const [isDestinationEditLoading, setIsDestinationEditLoading] =
        useState(false);
    const [isDestinationSaveLoading, setIsDestinationSaveLoading] =
        useState(false);
    const [isDestinationError, setIsDestinationError] = useState(false);

    const handleOpenDestinationPages = async () => {
        if (destinationPagesShow === false) {
            setIsDestinationEditLoading(true);
            const response = await getDestinationsByConnectionId(
                params.id && params.id,
            );
            setIsDestinationEditLoading(false);
            if (response.status && Array.isArray(response.data)) {
                setDestinationPagesShow(true);
                setIsDestinationError(false);
                const currentDest = singleConnection?.destination;
                const currentDestTypeCode =
                    currentDest?.destination_type_code || currentDest?.type;
                const typeCode =
                    allLeadDestinations?.find(
                        (t) =>
                            t.code === currentDestTypeCode ||
                            t.name === currentDestTypeCode,
                    )?.code ??
                    currentDest?.destination_type_code ??
                    currentDest?.type ??
                    "";
                const currentItem = currentDest
                    ? {
                          id: currentDest.id,
                          name: currentDest.name ?? currentDest.details?.name,
                          type: currentDest.type,
                          destination_type_code:
                              typeCode ||
                              currentDest.destination_type_code ||
                              currentDest.type,
                      }
                    : null;
                const apiList = response.data || [];
                const existingIds = new Set(apiList.map((d) => d.id));
                const list =
                    currentItem && !existingIds.has(currentItem.id)
                        ? [currentItem, ...apiList]
                        : apiList;
                setConnectionDestinationList(list);
                setSelectedConnectionDestinationType(typeCode);
                const selected =
                    currentItem || (list.length > 0 ? list[0] : null);
                setSelectedConnectionDestinationItem(selected);
                setSelectedConnectionDestination(selected?.id ?? "");
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message || "Failed to load destinations",
                    icon: "error",
                });
                setDestinationPagesShow(false);
            }
        } else {
            setDestinationPagesShow(false);
        }
    };

    const handleChangeDestination = (e) => {
        const id = e?.target?.value;
        const listByType = connectionDestinationList.filter(
            (d) =>
                (d.destination_type_code || d.type) ===
                selectedConnectionDestinationType,
        );
        const item = listByType.find((d) => d.id === Number(id));
        setSelectedConnectionDestinationItem(item || null);
        setSelectedConnectionDestination(
            id !== "" && id != null ? Number(id) : "",
        );
        setIsDestinationError(false);
    };

    const handleSaveDestinationEdit = async () => {
        const hasType = !!selectedConnectionDestinationType;
        if (!hasType) {
            return;
        }
        if (!selectedConnectionDestinationItem) {
            setIsDestinationError(true);

            return;
        }
        setIsDestinationError(false);
        setIsDestinationSaveLoading(true);
        const response = await editDestinationByConnectionId(
            params.id && params.id,
            {
                destination_id: selectedConnectionDestinationItem.id,
                destination_type:
                    selectedConnectionDestinationItem.destination_type_code ||
                    selectedConnectionDestinationItem.type,
            },
        );
        setIsDestinationSaveLoading(false);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message || "Destination updated",
                icon: "success",
                timer: 3000,
            });
            setDestinationPagesShow(false);
            dispatch(getSingleConnectionRedux(params.id && params.id));
        } else {
            setIsDestinationSaveLoading(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message || "Failed to update destination",
                icon: "error",
            });
        }
    };

    const getSelectableForms = (selectedForms) => {
        if (selectedForms) {
            let selectableForms = getSelectForms(
                selectedConnectionSourceItem,
                selectedConnectionPage,
            );
            return selectedForms.filter((id) =>
                selectableForms.some((item) => item.id === id),
            );
        } else {
            return [];
        }
    };

    const [destinationFields, setDestinationFields] = useState();
    const [allDestinationFields, setAllDestinationFields] = useState();

    // Create a stable filter function for destination fields autocomplete
    const destinationFilterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option) => option.label || option.name,
    });

    // Memoize destination fields to prevent duplicates
    const memoizedDestinationFields = useMemo(() => {
        const fields = destinationFields || [];
        return fields;
    }, [destinationFields]);

    useEffect(() => {
        if (newLeadDestination !== null) {
            if (
                typeof newLeadDestination === "object" ||
                Array.isArray(newLeadDestination)
            ) {
                let newDestinationFields = Object.values(newLeadDestination)
                    .map((item) => ({ label: item.name, value: item.type }))
                    .filter(
                        (option) =>
                            !matchFields.some(
                                (field) =>
                                    field.destination_field === option.value,
                            ),
                    );
                setDestinationFields(
                    newDestinationFields.filter((x) => x.value !== ""),
                );
                let newAllDestinationFields = [
                    ...Object.values(newLeadDestination).map((item) => ({
                        label: item.name,
                        value: item.type,
                    })),
                    ...matchFields.map((item) => ({
                        label: item.destination_label || item.destination_field,
                        value: item.destination_field,
                    })),
                ];
                setAllDestinationFields(
                    newAllDestinationFields.filter((x) => x.value !== ""),
                );
            }
        }
    }, [newLeadDestination]);
    useEffect(() => {
        if (
            Array.isArray(allDestinationFields) &&
            allDestinationFields.length > 0
        ) {
            setDestinationFields(
                allDestinationFields.filter(
                    (option) =>
                        !matchFields.some(
                            (field) => field.destination_field === option.value,
                        ),
                ),
            );
        }
    }, [matchFields]);

    const goBack = () => {
        window.history.back();
    };

    const [editFilterOptions, setEditFilterOptions] = useState([]);
    const [isSaveFilterLoading, setIsSaveFilterLoading] = useState(false);

    const handleFiltersSave = async (filters) => {
        setIsSaveFilterLoading(true);
        try {
            let connectionId = singleConnection?.connection_detail?.id;
            let fieldMappingId =
                singleConnection?.template?.field_mappings[0]?.id;
            let response = await saveFilters(connectionId, fieldMappingId, {
                filters:
                    Object.keys(filters).length &&
                    filters?.comparings.length > 0
                        ? filters
                        : null,
            });
            if (response.status) {
                setFilterShowStatus(false);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message && response.message,
                    icon: "success",
                    timer: 3000,
                });
                dispatch(getSingleConnectionRedux(params.id && params.id));
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } finally {
            setIsSaveFilterLoading(false);
        }
    };
    const filterOnClose = () => {
        if (
            singleConnection?.template?.field_mappings?.[0]?.filters
                ?.comparings === undefined
        ) {
            setConnectionFilters([]);
        } else {
            setConnectionFilters(
                singleConnection?.template?.field_mappings?.[0]?.filters
                    ?.comparings,
            );
        }
        setSelectedOperator(
            singleConnection?.template?.field_mappings?.[0]?.filters?.operator,
        );
    };

    const [optionsHover, setOptionsHover] = useState(null);

    const handleOpenOptions = (index) => {
        setMatchFields((prev) => {
            const newList = [...prev];
            newList[index].options.isOpen = !newList[index].options.isOpen;
            return newList;
        });
    };

    const [isComparisationFieldModalOpen, setIsComparisationFieldModalOpen] =
        useState(null);
    const [comparisationFieldIndex, setComparisationFieldIndex] =
        useState(null);

    const handleClickComparisationPart = (index) => {
        setIsComparisationFieldModalOpen(true);
        setComparisationFieldIndex(index);
        setMatchFields((prev) => {
            const newList = [...prev];
            newList[index].options.isOpen = !newList[index].options.isOpen;
            return newList;
        });
    };

    const handleAddedPrefix = (e, index) => {
        const { value } = e.target;
        setMatchFields((prev) => {
            const newList = [...prev];
            newList[index].prefix = value;
            return newList;
        });
    };
    const handleAddedDefaults = (e, index) => {
        const { value } = e.target;
        setMatchFields((prev) => {
            const newList = [...prev];
            newList[index].default = value;
            return newList;
        });
    };

    const isNumber = (val) => typeof val === "number";

    const sourceSelectRef = useRef(null);
    const [isSourceSelected, setIsSourceSelected] = useState(false);

    const GetOptionValueView = (options, key) => {
        if (
            typeof key === "string" &&
            key !== "" &&
            options &&
            options.length > 0
        ) {
            let label = options?.find((x) => x?.value === key)?.label || "";
            return {
                label: label,
                value: key,
            };
        } else {
            return {
                label: "",
                value: "none",
            };
        }
    };

    const getSelectOptions = (options) => {
        if (options && options.length > 0) {
            return options
                ?.filter((option) => option?.code !== "gglw")
                .map((x) => ({ label: x.name, value: x.code }));
        } else {
            return [];
        }
    };

    const getDestinationTypeSelectOptions = (options) => {
        if (options && options.length > 0) {
            return options.map((x) => ({ label: x.name, value: x.code }));
        }
        return [];
    };

    const getSelectForms = (pageItem, pageValue) => {
        if (!Array.isArray(pageItem?.pages)) {
            return [];
        }
        let pageList = pageItem.pages.flatMap((item) =>
            Array.isArray(item?.forms)
                ? item.forms.map((x) => ({
                      id: x?.id,
                      name: x?.name,
                      parent_id: item?.id,
                  }))
                : [],
        );
        if (pageList && Array.isArray(pageValue)) {
            pageList = pageList.filter((x) => pageValue.includes(x?.parent_id));
            return pageList || [];
        } else {
            return [];
        }
    };

    return (
        <PageContainer
            loading={
                loading === "idle" ||
                loading === "loading" ||
                (isLoadingFailedLeads && failedLeads.length === 0)
            }
            hasContent={Boolean(singleConnection?.connection_detail)}
            showBackButton={true}
            onBack={goBack}
            title="Connection Detail"
            subtitle={
                <InlineConnectionNameEdit
                    connectionId={params.id}
                    name={singleConnection?.connection_detail?.name}
                    loading={
                        (loading === "idle" || loading === "loading") &&
                        !singleConnection?.connection_detail?.name
                    }
                />
            }
            additionalButtons={
                <Stack
                    direction="row"
                    gap={2}
                    flexWrap="wrap"
                    alignItems="center"
                >
                    {getSourceName(singleConnection?.source?.source_type) ===
                        "Source API" && (
                        <StandartButton
                            onClick={handleGetDocumentButton}
                            color="primary"
                            variant={ButtonVariant.BLACK_OUTLINED}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                whiteSpace="nowrap"
                            >
                                <OpenInNewIcon />
                                <span>Implementation Guide</span>
                            </Box>
                        </StandartButton>
                    )}
                    <StandartButton
                        to={`${routes.leadDebug}/${params.id}`}
                        color="primary"
                        variant={ButtonVariant.BLACK_OUTLINED}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            {singleConnection?.connection_detail?.debug_mode ===
                                1 && <PulsingDot />}
                            {!singleConnection?.connection_detail
                                ?.debug_mode && <BugReportIcon />}
                            <span>Debug</span>
                        </Box>
                    </StandartButton>
                    <StandartButton
                        to={`${routes.leadConnectionReports}/${params.id}`}
                        color="primary"
                        variant={ButtonVariant.BLACK_OUTLINED}
                        className="tour-reports-btn"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <AnalyticsOutlinedIcon />
                            <span>Reports</span>
                        </Box>
                    </StandartButton>
                </Stack>
            }
            containerClassName="tracker-detail-area"
        >
            {isFailedLeadsModalOpen && (
                <FailedLeadsModal
                    destination={
                        singleConnection?.destination?.destination_type ||
                        singleConnection?.destination?.type
                    }
                    failedLeads={failedLeads}
                    duplicateLeads={duplicateLeads}
                    onClose={handleCloseLeadIssues}
                    handleGetFailedLeads={handleGetFailedLeads}
                    handleGetDuplicateLeads={handleGetDuplicateLeads}
                    isLoadingFailedLeads={isLoadingFailedLeads}
                    failedLeadsPagination={failedLeadsPagination}
                    duplicateLeadsPagination={duplicateLeadsPagination}
                    setFailedLeadsPagination={setFailedLeadsPagination}
                    setDuplicateLeadsPagination={setDuplicateLeadsPagination}
                    firstActiveTab={activeTab}
                />
            )}

            <div>
                <Grid
                    container
                    rowSpacing={1}
                    className="trackers-area connections-grid-area mb-0"
                >
                    <Grid
                        size={{
                            xs: 5,
                            sm: 12,
                            md: 12,
                            lg: 5,
                        }}
                    >
                        <div className="title connection-card-title mb-3">
                            <span>
                                {language?.connection_detail?.source ??
                                    "Source"}
                            </span>
                            {!isSourceExpired && (
                                <Box marginLeft="auto">
                                    <StandartButton
                                        variant={ButtonVariant.ICON_BUTTON}
                                        iconButton
                                        disabled={isSourceEditLoading}
                                        isLoading={isSourceEditLoading}
                                        onClick={handleOpenSourcePages}
                                        className="tour-source-edit-btn"
                                        aria-label={
                                            sourcePagesShow
                                                ? "Close source edit"
                                                : "Edit source"
                                        }
                                    >
                                        {sourcePagesShow ? (
                                            <CloseOutlinedIcon />
                                        ) : (
                                            <EditOutlinedIcon />
                                        )}
                                    </StandartButton>
                                </Box>
                            )}
                        </div>
                        <Card
                            className={`dashboard-tree-source-item no-effect dashboard-tree-source-item-1 lead-item`}
                            style={{
                                boxShadow: "none",
                                border: "1px solid #E7E7E7",
                                borderRadius: "10px",
                                position: "relative",
                                borderColor: isSourceExpired
                                    ? "red"
                                    : "#E7E7E7",
                            }}
                        >
                            {sourcePagesShow ? (
                                <>
                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start flex-column"
                                    >
                                        <Form.Group
                                            className="w-full mb-3"
                                            controlId="exampleForm.ControlInput1"
                                        >
                                            <FormControl className="w-full">
                                                <Autocomplete
                                                    ref={sourceSelectRef}
                                                    label={
                                                        language?.lead
                                                            ?.select_source_type ??
                                                        "Select Source Type"
                                                    }
                                                    value={GetOptionValueView(
                                                        getSelectOptions(
                                                            allLeadSources,
                                                        ),
                                                        selectedConnectionSourceType,
                                                    )}
                                                    className="select-image-list tour-source-select"
                                                    options={
                                                        allLeadSources &&
                                                        getSelectOptions(
                                                            allLeadSources,
                                                        )
                                                    }
                                                    disableClearable
                                                    getOptionLabel={(option) =>
                                                        option.label || ""
                                                    }
                                                    onChange={(
                                                        event,
                                                        selectedOpt,
                                                    ) => {
                                                        setSelectedConnectionSourceType(
                                                            selectedOpt?.value,
                                                        );
                                                        setSelectedConnectionSource(
                                                            "",
                                                        );
                                                        setSelectedConnectionSourceItem(
                                                            null,
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <div>
                                                            <TextField
                                                                {...params}
                                                                label={
                                                                    language
                                                                        ?.source_modal
                                                                        ?.select_source_type ??
                                                                    "Select Source Type"
                                                                }
                                                                error={
                                                                    isSourceSelected
                                                                }
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment:
                                                                        (
                                                                            <img
                                                                                style={{
                                                                                    width: "25px",
                                                                                    margin: "5px",
                                                                                    marginTop:
                                                                                        "10px",
                                                                                }}
                                                                                src={getSourceIcon(
                                                                                    params
                                                                                        ?.inputProps
                                                                                        ?.value,
                                                                                )}
                                                                            />
                                                                        ),
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                    renderOption={(
                                                        props,
                                                        option,
                                                    ) => (
                                                        <Box
                                                            component="li"
                                                            sx={{
                                                                "& > img": {
                                                                    mr: 2,
                                                                    flexShrink: 0,
                                                                },
                                                            }}
                                                            {...props}
                                                        >
                                                            <img
                                                                width="25"
                                                                src={getSourceIcon(
                                                                    option?.value,
                                                                )}
                                                            />
                                                            {option.label}
                                                        </Box>
                                                    )}
                                                ></Autocomplete>
                                            </FormControl>
                                        </Form.Group>
                                        {selectedConnectionSourceType !== "" &&
                                            connectionSourceList &&
                                            connectionSourceList.filter(
                                                (x) =>
                                                    x.source_type ===
                                                    selectedConnectionSourceType,
                                            ).length > 0 && (
                                                <>
                                                    <Form.Group
                                                        className="w-full"
                                                        controlId="exampleForm.ControlInput1"
                                                    >
                                                        <FormControl className="w-full">
                                                            <Select
                                                                label={
                                                                    language
                                                                        ?.connection_detail
                                                                        ?.select_source ??
                                                                    "Select Source"
                                                                }
                                                                onChange={
                                                                    handleChangeSource
                                                                }
                                                                value={
                                                                    selectedConnectionSource
                                                                }
                                                            >
                                                                {connectionSourceList &&
                                                                    connectionSourceList
                                                                        .filter(
                                                                            (
                                                                                x,
                                                                            ) =>
                                                                                x.source_type ===
                                                                                selectedConnectionSourceType,
                                                                        )
                                                                        .map(
                                                                            (
                                                                                item,
                                                                            ) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        item.id
                                                                                    }
                                                                                    value={
                                                                                        item.id
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        item.name
                                                                                    }
                                                                                </MenuItem>
                                                                            ),
                                                                        )}
                                                            </Select>
                                                        </FormControl>
                                                    </Form.Group>
                                                    {selectedConnectionSourceItem?.source_type ===
                                                        "tiktok" &&
                                                        selectedConnectionSourceItem
                                                            ?.pages.length >
                                                            0 && (
                                                            <Form.Group
                                                                className="w-full mt-3"
                                                                controlId="exampleForm.ControlInput1"
                                                            >
                                                                <MuiSelect
                                                                    value={
                                                                        selectedConnectionPage
                                                                    }
                                                                    options={selectedConnectionSourceItem?.pages?.map(
                                                                        (
                                                                            page,
                                                                        ) => ({
                                                                            label: page?.name,
                                                                            value: page?.id,
                                                                        }),
                                                                    )}
                                                                    setValue={
                                                                        handleChangePage
                                                                    }
                                                                    label={
                                                                        language
                                                                            ?.connection_detail
                                                                            ?.select_pages ??
                                                                        "Select Pages"
                                                                    }
                                                                    labelId="connection-tiktok-source-page-select-label"
                                                                    multiple
                                                                    withChip
                                                                    renderSelectAll
                                                                />
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    gap={0.5}
                                                                >
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={
                                                                                    addNewFormsValue
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) =>
                                                                                    setAddNewFormsValue(
                                                                                        e
                                                                                            .target
                                                                                            .checked,
                                                                                    )
                                                                                }
                                                                            />
                                                                        }
                                                                        label="Add new forms automatically."
                                                                    />
                                                                    <Tooltip
                                                                        title="When enabled, the system automatically fetches and checks the forms from the pages you connected. This helps keep your form list up to date without manual setup."
                                                                        placement="top"
                                                                        arrow
                                                                    >
                                                                        <HelpOutlineIcon
                                                                            sx={{
                                                                                fontSize:
                                                                                    "18px",
                                                                                color: "#666",
                                                                                cursor: "help",
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Form.Group>
                                                        )}
                                                    {selectedConnectionSourceItem?.source_type ===
                                                        "fl" &&
                                                        selectedConnectionSourceItem
                                                            ?.pages.length >
                                                            0 && (
                                                            <Form.Group
                                                                className="w-full mt-3"
                                                                controlId="exampleForm.ControlInput1"
                                                            >
                                                                <MuiSelect
                                                                    value={
                                                                        selectedConnectionPage
                                                                    }
                                                                    options={selectedConnectionSourceItem?.pages?.map(
                                                                        (
                                                                            page,
                                                                        ) => ({
                                                                            label: page?.name,
                                                                            value: page?.id,
                                                                        }),
                                                                    )}
                                                                    setValue={
                                                                        handleChangePage
                                                                    }
                                                                    label={
                                                                        language
                                                                            ?.connection_detail
                                                                            ?.select_pages ??
                                                                        "Select Pages"
                                                                    }
                                                                    labelId="connection-fb-source-page-select-label"
                                                                    multiple
                                                                    withChip
                                                                    renderSelectAll
                                                                    className="mb-3"
                                                                    disableCloseOnSelect
                                                                />
                                                                <MuiSelect
                                                                    value={
                                                                        selectedConnectionForm
                                                                    }
                                                                    options={getSelectForms(
                                                                        selectedConnectionSourceItem,
                                                                        selectedConnectionPage,
                                                                    )?.map(
                                                                        (
                                                                            form,
                                                                        ) => ({
                                                                            label: form?.name,
                                                                            value: form?.id,
                                                                        }),
                                                                    )}
                                                                    setValue={(
                                                                        value,
                                                                    ) =>
                                                                        setSelectedConnectionForm(
                                                                            value,
                                                                        )
                                                                    }
                                                                    label={
                                                                        language
                                                                            ?.connection_detail
                                                                            ?.select_forms ??
                                                                        "Select Forms"
                                                                    }
                                                                    labelId="connection-fb-source-page-select-label"
                                                                    multiple
                                                                    withChip
                                                                    renderSelectAll
                                                                    disableCloseOnSelect
                                                                />
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    gap={0.5}
                                                                >
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={
                                                                                    addNewFormsValue
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) =>
                                                                                    setAddNewFormsValue(
                                                                                        e
                                                                                            .target
                                                                                            .checked,
                                                                                    )
                                                                                }
                                                                            />
                                                                        }
                                                                        label="Add new forms automatically."
                                                                    />
                                                                    <Tooltip
                                                                        title="When enabled, the system automatically fetches and checks the forms from the pages you connected. This helps keep your form list up to date without manual setup."
                                                                        placement="top"
                                                                        arrow
                                                                    >
                                                                        <HelpOutlineIcon
                                                                            sx={{
                                                                                fontSize:
                                                                                    "18px",
                                                                                color: "#666",
                                                                                cursor: "help",
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Form.Group>
                                                        )}
                                                    <Stack
                                                        className="w-full mt-3"
                                                        gap={2}
                                                    >
                                                        {selectedConnectionSourceItem?.source_type ===
                                                            "tiktok" && (
                                                            <StandartButton
                                                                type="button"
                                                                onClick={
                                                                    handleRefreshTiktokForms
                                                                }
                                                                variant={
                                                                    ButtonVariant.WARNING_OUTLINED
                                                                }
                                                                isLoading={
                                                                    isTiktokRefreshLoading
                                                                }
                                                                disabled={
                                                                    isSourceSaveLoading ||
                                                                    isTiktokRefreshLoading
                                                                }
                                                                sx={{
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {language
                                                                    ?.connection_detail
                                                                    ?.refresh_forms ??
                                                                    "Refresh Forms"}
                                                            </StandartButton>
                                                        )}
                                                        {singleConnection
                                                            ?.source
                                                            ?.source_type ===
                                                            "Facebook Lead" &&
                                                            GetOptionValueView(
                                                                getSelectOptions(
                                                                    allLeadSources,
                                                                ),
                                                                selectedConnectionSourceType,
                                                            )?.value ===
                                                                "fl" && (
                                                                <>
                                                                    <Stack
                                                                        direction={{
                                                                            xs: "column",
                                                                            sm: "row",
                                                                        }}
                                                                        gap={1}
                                                                        className="w-full"
                                                                        sx={{
                                                                            width: "100%",
                                                                        }}
                                                                    >
                                                                        <StandartButton
                                                                            type="button"
                                                                            onClick={
                                                                                handleRefreshSource
                                                                            }
                                                                            variant={
                                                                                ButtonVariant.WARNING_OUTLINED
                                                                            }
                                                                            isLoading={
                                                                                isRefreshSourceLoading
                                                                            }
                                                                            wrapperSx={{
                                                                                flex: 1,
                                                                                width: "100%",
                                                                            }}
                                                                            sx={{
                                                                                width: "100%",
                                                                            }}
                                                                        >
                                                                            {language
                                                                                ?.connection_detail
                                                                                ?.save_changes ??
                                                                                "Refresh"}
                                                                        </StandartButton>
                                                                        <Box
                                                                            position="relative"
                                                                            sx={{
                                                                                flex: 1,
                                                                                width: "100%",
                                                                            }}
                                                                        >
                                                                            <StandartButton
                                                                                isLoading={
                                                                                    isAddPageLoading
                                                                                }
                                                                                onClick={
                                                                                    handleAddPageClick
                                                                                }
                                                                                variant={
                                                                                    ButtonVariant.WARNING_OUTLINED
                                                                                }
                                                                                disabled={
                                                                                    isRefreshSourceLoading ||
                                                                                    isAddPageLoading
                                                                                }
                                                                                sx={{
                                                                                    width: "100%",
                                                                                }}
                                                                                style={{
                                                                                    minWidth:
                                                                                        "unset !important",
                                                                                }}
                                                                            >
                                                                                {language
                                                                                    ?.connection_detail
                                                                                    ?.add_page ??
                                                                                    "Add Page"}
                                                                            </StandartButton>
                                                                        </Box>
                                                                    </Stack>
                                                                </>
                                                            )}
                                                        <Box
                                                            position="relative"
                                                            className="w-full"
                                                        >
                                                            {isSourceSaveLoading && (
                                                                <div className="circle-loader">
                                                                    <ClipLoader
                                                                        color="#fff"
                                                                        size={
                                                                            18
                                                                        }
                                                                    />
                                                                </div>
                                                            )}
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                onClick={
                                                                    handleSaveSourceEdit
                                                                }
                                                                disabled={
                                                                    isTiktokRefreshLoading
                                                                }
                                                                type="button"
                                                                className="w-full"
                                                            >
                                                                {language
                                                                    ?.buttons
                                                                    ?.save ??
                                                                    "Save"}
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                </>
                                            )}
                                    </CardContent>
                                </>
                            ) : (
                                <>
                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            flexWrap: "wrap",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <Typography className="mr-3">
                                            <div className="item-icon-area">
                                                <LogoIcon
                                                    src={getSourceIcon(
                                                        singleConnection?.source
                                                            ?.source_type,
                                                    )}
                                                />
                                            </div>
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 18,
                                                fontWeight: "700",
                                                fontFamily:
                                                    "Nunito, sans-serif",
                                                color: isSourceExpired
                                                    ? "red"
                                                    : "black",
                                            }}
                                        >
                                            {
                                                singleConnection?.source
                                                    ?.source_type
                                            }
                                            {isSourceExpired && (
                                                <SourceErrorTooltip
                                                    placement="top"
                                                    style={{
                                                        marginLeft: "10px",
                                                    }}
                                                    title={
                                                        <React.Fragment>
                                                            <Typography color="white">
                                                                {"Error"}
                                                            </Typography>
                                                            <span>
                                                                {
                                                                    "Since the Access Token is expired, you must reconnect the source."
                                                                }
                                                            </span>
                                                        </React.Fragment>
                                                    }
                                                >
                                                    <WarningIcon
                                                        style={{
                                                            verticalAlign:
                                                                "sub",
                                                            marginLeft: "10px",
                                                            color: "red",
                                                        }}
                                                    />
                                                </SourceErrorTooltip>
                                            )}
                                            {singleConnection?.connection_detail
                                                ?.check_new_forms &&
                                                (singleConnection?.source
                                                    ?.source_type ===
                                                    "Facebook Lead" ||
                                                    singleConnection?.source
                                                        ?.source_type ===
                                                        "Tiktok Lead Generation") && (
                                                    <Box>
                                                        <Stack
                                                            direction="row"
                                                            gap="8px"
                                                            color="#45c300"
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                style={{
                                                                    width: "8px",
                                                                    height: "8px",
                                                                    borderRadius:
                                                                        "50%",
                                                                    background:
                                                                        "#45c300",
                                                                }}
                                                            ></Box>
                                                            <Box
                                                                fontSize="12px"
                                                                fontWeight="normal"
                                                            >
                                                                Form Check
                                                                Enabled
                                                            </Box>
                                                            <Tooltip
                                                                title="When enabled, the system automatically fetches and checks the forms from the pages you connected. This helps keep your form list up to date without manual setup."
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <HelpOutlineIcon
                                                                    sx={{
                                                                        fontSize:
                                                                            "16px",
                                                                        color: "#45c300",
                                                                        cursor: "help",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Stack>
                                                    </Box>
                                                )}
                                        </Typography>
                                    </CardContent>
                                    <Stack
                                        direction="row"
                                        alignItems="flex-end"
                                        width="100%"
                                        className="connection-detail-stack"
                                    >
                                        <CardContent
                                            style={{
                                                padding: "25px 20px",
                                                width: "100%",
                                                minWidth: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "stretch",
                                            }}
                                        >
                                            <div className="lead-service-info-area">
                                                {getSourceName(
                                                    singleConnection?.source
                                                        ?.source_type,
                                                ) !== "Facebook Lead" &&
                                                getSourceName(
                                                    singleConnection?.source
                                                        ?.source_type,
                                                ) !==
                                                    "Tiktok Lead Generation" &&
                                                getSourceName(
                                                    singleConnection?.source
                                                        ?.source_type,
                                                ) !==
                                                    "Linkedin Lead Generation" ? (
                                                    <span
                                                        style={{
                                                            display: "block",
                                                            fontSize: "14px",
                                                            fontWeight: "500",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display:
                                                                    "block",
                                                                fontSize:
                                                                    "12px",
                                                                color: "#ABABAB",
                                                                fontWeight:
                                                                    "400",
                                                            }}
                                                        >
                                                            {language
                                                                ?.connections
                                                                ?.name ??
                                                                "Name"}
                                                        </span>
                                                        <span
                                                            style={{
                                                                color: "black",
                                                            }}
                                                        >
                                                            {
                                                                singleConnection
                                                                    ?.source
                                                                    ?.details
                                                                    ?.name
                                                            }
                                                        </span>
                                                        {singleConnection
                                                            ?.source?.details
                                                            ?.urls
                                                            ?.document_url && (
                                                            <>
                                                                <span
                                                                    style={{
                                                                        display:
                                                                            "block",
                                                                        fontSize:
                                                                            "12px",
                                                                        color: "#ABABAB",
                                                                        fontWeight:
                                                                            "400",
                                                                        marginTop:
                                                                            "8px",
                                                                    }}
                                                                >
                                                                    {language
                                                                        ?.connections
                                                                        ?.name ??
                                                                        "Document URL"}
                                                                </span>
                                                                <a
                                                                    style={{
                                                                        wordBreak:
                                                                            "break-all",
                                                                    }}
                                                                    target="_blank"
                                                                    href={
                                                                        singleConnection
                                                                            ?.source
                                                                            ?.details
                                                                            ?.urls
                                                                            ?.document_url
                                                                    }
                                                                >
                                                                    {
                                                                        singleConnection
                                                                            ?.source
                                                                            ?.details
                                                                            ?.urls
                                                                            ?.document_url
                                                                    }
                                                                </a>
                                                            </>
                                                        )}
                                                        {singleConnection
                                                            ?.source?.details
                                                            ?.urls
                                                            ?.webhook_url && (
                                                            <>
                                                                <span
                                                                    style={{
                                                                        display:
                                                                            "block",
                                                                        fontSize:
                                                                            "12px",
                                                                        color: "#ABABAB",
                                                                        fontWeight:
                                                                            "400",
                                                                        marginTop:
                                                                            "8px",
                                                                    }}
                                                                >
                                                                    {language
                                                                        ?.connections
                                                                        ?.name ??
                                                                        "Webhook URL"}
                                                                </span>
                                                                <a
                                                                    style={{
                                                                        wordBreak:
                                                                            "break-all",
                                                                    }}
                                                                    target="_blank"
                                                                    href={
                                                                        singleConnection
                                                                            ?.source
                                                                            ?.details
                                                                            ?.urls
                                                                            ?.webhook_url
                                                                    }
                                                                >
                                                                    {
                                                                        singleConnection
                                                                            ?.source
                                                                            ?.details
                                                                            ?.urls
                                                                            ?.webhook_url
                                                                    }
                                                                </a>
                                                            </>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <>
                                                            <>
                                                                {singleConnection
                                                                    ?.source
                                                                    ?.name && (
                                                                    <span
                                                                        style={{
                                                                            display:
                                                                                "block",
                                                                            fontSize:
                                                                                "14px",
                                                                            color: "black",
                                                                            fontWeight:
                                                                                "500",
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                display:
                                                                                    "block",
                                                                                fontSize:
                                                                                    "12px",
                                                                                color: "#ABABAB",
                                                                                fontWeight:
                                                                                    "400",
                                                                            }}
                                                                        >
                                                                            {language
                                                                                ?.connections
                                                                                ?.name ??
                                                                                "Name"}
                                                                        </span>
                                                                        <span>
                                                                            {
                                                                                singleConnection
                                                                                    ?.source
                                                                                    ?.name
                                                                            }
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </>

                                                            {/** XXXXX */}
                                                            {Array.isArray(
                                                                singleConnection
                                                                    ?.source
                                                                    ?.pages,
                                                            ) &&
                                                                singleConnection
                                                                    .source
                                                                    .pages
                                                                    .length >
                                                                    0 && (
                                                                    <Box width="100%">
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "block",
                                                                                fontSize:
                                                                                    "12px",
                                                                                color: "#ABABAB",
                                                                                fontWeight:
                                                                                    "400",
                                                                            }}
                                                                        >
                                                                            {getSourceName(
                                                                                singleConnection
                                                                                    ?.source
                                                                                    ?.source_type,
                                                                            ) ===
                                                                            "Linkedin Lead Generation"
                                                                                ? "Ad Account"
                                                                                : "Pages"}
                                                                        </div>
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="flex-end"
                                                                            gap={
                                                                                1
                                                                            }
                                                                            width="100%"
                                                                        >
                                                                            <Stack
                                                                                gap={
                                                                                    1
                                                                                }
                                                                                direction="row"
                                                                                flexWrap="wrap"
                                                                                flex={
                                                                                    1
                                                                                }
                                                                            >
                                                                                {singleConnection.source.pages.map(
                                                                                    (
                                                                                        page,
                                                                                        index,
                                                                                    ) => {
                                                                                        return (
                                                                                            index <
                                                                                                3 && (
                                                                                                <Chip
                                                                                                    className="chip-leads-page"
                                                                                                    variant="filled"
                                                                                                    label={
                                                                                                        page?.name
                                                                                                    }
                                                                                                    style={{
                                                                                                        width: "fit-content",
                                                                                                    }}
                                                                                                />
                                                                                            )
                                                                                        );
                                                                                    },
                                                                                )}
                                                                                {singleConnection
                                                                                    ?.source
                                                                                    ?.pages
                                                                                    ?.length >
                                                                                    3 && (
                                                                                    <Chip
                                                                                        className="chip-more chip-leads-page"
                                                                                        variant="filled"
                                                                                        label={
                                                                                            <Stack direction="row">
                                                                                                <Box>
                                                                                                    {singleConnection
                                                                                                        ?.source
                                                                                                        ?.pages
                                                                                                        ?.length -
                                                                                                        3}
                                                                                                </Box>
                                                                                                <Box>
                                                                                                    +
                                                                                                    More
                                                                                                </Box>
                                                                                            </Stack>
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            </Stack>
                                                                            {isTiktokSource &&
                                                                                renderSourceExpandButton()}
                                                                        </Stack>
                                                                    </Box>
                                                                )}
                                                            {singleConnection
                                                                ?.source
                                                                ?.pages &&
                                                                getSourceName(
                                                                    singleConnection
                                                                        ?.source
                                                                        ?.source_type,
                                                                ) ===
                                                                    "Facebook Lead" && (
                                                                    <Box width="100%">
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "block",
                                                                                fontSize:
                                                                                    "12px",
                                                                                color: "#ABABAB",
                                                                                fontWeight:
                                                                                    "400",
                                                                            }}
                                                                        >
                                                                            {language
                                                                                ?.connections
                                                                                ?.forms ??
                                                                                "Forms"}
                                                                        </div>
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="flex-end"
                                                                            gap={
                                                                                1
                                                                            }
                                                                            width="100%"
                                                                        >
                                                                            <Stack
                                                                                gap={
                                                                                    1
                                                                                }
                                                                                direction="row"
                                                                                flexWrap="wrap"
                                                                                flex={
                                                                                    1
                                                                                }
                                                                            >
                                                                                {singleConnection
                                                                                    ?.source
                                                                                    ?.pages &&
                                                                                    singleConnection?.source?.pages
                                                                                        .flatMap(
                                                                                            (
                                                                                                page,
                                                                                            ) =>
                                                                                                page.forms,
                                                                                        )
                                                                                        .map(
                                                                                            (
                                                                                                form,
                                                                                                index,
                                                                                            ) => {
                                                                                                return (
                                                                                                    index <
                                                                                                        3 && (
                                                                                                        <Chip
                                                                                                            className="chip-leads-page"
                                                                                                            variant="filled"
                                                                                                            label={
                                                                                                                form?.name
                                                                                                            }
                                                                                                            style={{
                                                                                                                width: "fit-content",
                                                                                                            }}
                                                                                                        />
                                                                                                    )
                                                                                                );
                                                                                            },
                                                                                        )}
                                                                                {singleConnection
                                                                                    ?.source
                                                                                    ?.pages &&
                                                                                    singleConnection?.source?.pages.flatMap(
                                                                                        (
                                                                                            page,
                                                                                        ) =>
                                                                                            page.forms,
                                                                                    )
                                                                                        ?.length >
                                                                                        3 && (
                                                                                        <Chip
                                                                                            className="chip-more chip-leads-page"
                                                                                            variant="filled"
                                                                                            label={
                                                                                                <Stack direction="row">
                                                                                                    <Box>
                                                                                                        {singleConnection
                                                                                                            ?.source
                                                                                                            ?.pages &&
                                                                                                            singleConnection?.source?.pages.flatMap(
                                                                                                                (
                                                                                                                    page,
                                                                                                                ) =>
                                                                                                                    page.forms,
                                                                                                            )
                                                                                                                ?.length -
                                                                                                                3}
                                                                                                    </Box>
                                                                                                    <Box>
                                                                                                        +
                                                                                                        More
                                                                                                    </Box>
                                                                                                </Stack>
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                            </Stack>
                                                                            {isFacebookSource &&
                                                                                renderSourceExpandButton()}
                                                                        </Stack>
                                                                    </Box>
                                                                )}

                                                            {singleConnection
                                                                ?.source
                                                                ?.pages &&
                                                                getSourceName(
                                                                    singleConnection
                                                                        ?.source
                                                                        ?.source_type,
                                                                ) ===
                                                                    "Linkedin Lead Generation" && (
                                                                    <Box width="100%">
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "block",
                                                                                fontSize:
                                                                                    "12px",
                                                                                color: "#ABABAB",
                                                                                fontWeight:
                                                                                    "400",
                                                                            }}
                                                                        >
                                                                            {language
                                                                                ?.connections
                                                                                ?.forms ??
                                                                                "Forms"}
                                                                        </div>
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="flex-end"
                                                                            gap={
                                                                                1
                                                                            }
                                                                            width="100%"
                                                                        >
                                                                            <Stack
                                                                                gap={
                                                                                    1
                                                                                }
                                                                                direction="row"
                                                                                flexWrap="wrap"
                                                                                flex={
                                                                                    1
                                                                                }
                                                                            >
                                                                                {singleConnection
                                                                                    ?.source
                                                                                    ?.pages &&
                                                                                    singleConnection?.source?.pages
                                                                                        .flatMap(
                                                                                            (
                                                                                                page,
                                                                                            ) =>
                                                                                                page.forms,
                                                                                        )
                                                                                        .map(
                                                                                            (
                                                                                                form,
                                                                                                index,
                                                                                            ) => {
                                                                                                return (
                                                                                                    index <
                                                                                                        3 && (
                                                                                                        <Chip
                                                                                                            className="chip-leads-page"
                                                                                                            variant="filled"
                                                                                                            label={
                                                                                                                form?.name
                                                                                                            }
                                                                                                            style={{
                                                                                                                width: "fit-content",
                                                                                                            }}
                                                                                                        />
                                                                                                    )
                                                                                                );
                                                                                            },
                                                                                        )}
                                                                                {singleConnection
                                                                                    ?.source
                                                                                    ?.pages &&
                                                                                    singleConnection?.source?.pages.flatMap(
                                                                                        (
                                                                                            page,
                                                                                        ) =>
                                                                                            page.forms,
                                                                                    )
                                                                                        ?.length >
                                                                                        3 && (
                                                                                        <Chip
                                                                                            className="chip-more chip-leads-page"
                                                                                            variant="filled"
                                                                                            label={
                                                                                                <Stack direction="row">
                                                                                                    <Box>
                                                                                                        {singleConnection
                                                                                                            ?.source
                                                                                                            ?.pages &&
                                                                                                            singleConnection?.source?.pages.flatMap(
                                                                                                                (
                                                                                                                    page,
                                                                                                                ) =>
                                                                                                                    page.forms,
                                                                                                            )
                                                                                                                ?.length -
                                                                                                                3}
                                                                                                    </Box>
                                                                                                    <Box>
                                                                                                        +
                                                                                                        More
                                                                                                    </Box>
                                                                                                </Stack>
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                            </Stack>
                                                                            {isLinkedinSource &&
                                                                                renderSourceExpandButton()}
                                                                        </Stack>
                                                                    </Box>
                                                                )}
                                                        </>
                                                    </>
                                                )}
                                            </div>
                                            {leadIssues.length > 0 && (
                                                <LeadIssueList>
                                                    {leadIssues.map((issue) => (
                                                        <LeadIssueRow
                                                            key={issue.key}
                                                            ref={(element) => {
                                                                leadIssueRowRefs.current[
                                                                    issue.key
                                                                ] = element;
                                                            }}
                                                            label={issue.label}
                                                            count={issue.count}
                                                            severity={
                                                                issue.severity
                                                            }
                                                            icon={issue.icon}
                                                            onClick={() =>
                                                                openLeadIssues(
                                                                    issue.key,
                                                                    issue.tabIndex,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </LeadIssueList>
                                            )}
                                        </CardContent>
                                    </Stack>
                                    {isSourceExpired && (
                                        <ConnectionDetailReconnectButton
                                            reconnectType={
                                                singleConnection?.source
                                                    ?.source_type
                                            }
                                        />
                                    )}
                                </>
                            )}
                            <SourcePopover
                                setVisible={setIsSourcePopoverVisible}
                                visible={isSourcePopoverVisible}
                                ref={sourceCardRef}
                                isFacebook={
                                    getSourceName(
                                        singleConnection?.source?.source_type,
                                    ) === "Facebook Lead"
                                }
                                isTiktok={
                                    getSourceName(
                                        singleConnection?.source?.source_type,
                                    ) === "Tiktok Lead Generation"
                                }
                                sourceIcon={getSourceIcon(
                                    singleConnection?.source?.source_type,
                                )}
                                sourceName={
                                    singleConnection?.source?.source_type
                                }
                                pages={singleConnection?.source?.pages?.map(
                                    (page) => page?.name,
                                )}
                                pagesLabel={
                                    getSourceName(
                                        singleConnection?.source?.source_type,
                                    ) === "Linkedin Lead Generation"
                                        ? "Ad Account"
                                        : "Pages"
                                }
                                name={singleConnection?.source?.name}
                                forms={
                                    singleConnection?.source?.pages &&
                                    singleConnection?.source?.pages
                                        .flatMap((page) => page.forms)
                                        ?.map((form) => form?.name)
                                }
                            />
                        </Card>
                    </Grid>
                    <Grid
                        className="p-0"
                        size={{
                            xs: 2,
                            sm: 12,
                            md: 12,
                            lg: 2,
                        }}
                    >
                        <div className="lead-connection-center-area max-w-md:hidden"></div>
                    </Grid>
                    <Grid
                        size={{
                            xs: 5,
                            sm: 12,
                            md: 12,
                            lg: 5,
                        }}
                    >
                        <div className="title connection-card-title text-right mb-3">
                            <span>
                                {language?.connection_detail?.destination ??
                                    "Destination"}
                            </span>
                            {!isDestinationExpired && (
                                <Box marginLeft="auto">
                                    <StandartButton
                                        iconButton
                                        disabled={isDestinationEditLoading}
                                        isLoading={isDestinationEditLoading}
                                        onClick={handleOpenDestinationPages}
                                        className="tour-destination-edit-btn"
                                        aria-label={
                                            destinationPagesShow
                                                ? "Close destination edit"
                                                : "Edit destination"
                                        }
                                    >
                                        {destinationPagesShow ? (
                                            <CloseOutlinedIcon />
                                        ) : (
                                            <EditOutlinedIcon />
                                        )}
                                    </StandartButton>
                                </Box>
                            )}
                        </div>
                        <Card
                            className={`dashboard-tree-source-item no-effect dashboard-tree-source-item-1 lead-item`}
                            style={{
                                boxShadow: "none",
                                border: "1px solid #E7E7E7",
                                borderRadius: "10px",
                                borderColor: isDestinationExpired
                                    ? "red"
                                    : "#E7E7E7",
                            }}
                        >
                            {destinationPagesShow ? (
                                <>
                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start flex-column"
                                    >
                                        {isDestinationEditLoading ? (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                p={2}
                                            >
                                                <ClipLoader
                                                    color="#ed6c02"
                                                    size={24}
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                <Form.Group
                                                    className="w-full mb-3"
                                                    controlId="destination-edit-type-select"
                                                >
                                                    <FormControl className="w-full">
                                                        <Autocomplete
                                                            className="select-image-list tour-destination-type-select"
                                                            value={GetOptionValueView(
                                                                getDestinationTypeSelectOptions(
                                                                    allLeadDestinations,
                                                                ),
                                                                selectedConnectionDestinationType,
                                                            )}
                                                            options={
                                                                allLeadDestinations
                                                                    ? getDestinationTypeSelectOptions(
                                                                          allLeadDestinations,
                                                                      )
                                                                    : []
                                                            }
                                                            disableClearable
                                                            getOptionLabel={(
                                                                option,
                                                            ) =>
                                                                option?.label ||
                                                                ""
                                                            }
                                                            onChange={(
                                                                event,
                                                                selectedOpt,
                                                            ) => {
                                                                const typeCode =
                                                                    selectedOpt?.value ??
                                                                    "";
                                                                setSelectedConnectionDestinationType(
                                                                    typeCode,
                                                                );

                                                                setSelectedConnectionDestinationItem(
                                                                    null,
                                                                );
                                                                setSelectedConnectionDestination(
                                                                    null,
                                                                );
                                                                setIsDestinationError(
                                                                    false,
                                                                );
                                                            }}
                                                            renderInput={(
                                                                params,
                                                            ) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={
                                                                        language
                                                                            ?.connection_detail
                                                                            ?.select_destination_type ??
                                                                        "Select Destination Type"
                                                                    }
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment:
                                                                            (() => {
                                                                                const code =
                                                                                    selectedConnectionDestinationType ||
                                                                                    params
                                                                                        ?.inputProps
                                                                                        ?.value
                                                                                        ?.value;
                                                                                const iconSrc =
                                                                                    getDestinationIcon(
                                                                                        code,
                                                                                    );
                                                                                return iconSrc ? (
                                                                                    <img
                                                                                        alt=""
                                                                                        style={{
                                                                                            width: "25px",
                                                                                            margin: "5px",
                                                                                            marginTop:
                                                                                                "10px",
                                                                                        }}
                                                                                        src={
                                                                                            iconSrc
                                                                                        }
                                                                                    />
                                                                                ) : null;
                                                                            })(),
                                                                    }}
                                                                />
                                                            )}
                                                            renderOption={(
                                                                props,
                                                                option,
                                                            ) => (
                                                                <Box
                                                                    component="li"
                                                                    sx={{
                                                                        "& > img":
                                                                            {
                                                                                mr: 2,
                                                                                flexShrink: 0,
                                                                            },
                                                                    }}
                                                                    {...props}
                                                                >
                                                                    {getDestinationIcon(
                                                                        option?.value,
                                                                    ) && (
                                                                        <img
                                                                            width="25"
                                                                            alt=""
                                                                            src={getDestinationIcon(
                                                                                option?.value,
                                                                            )}
                                                                        />
                                                                    )}
                                                                    {
                                                                        option?.label
                                                                    }
                                                                </Box>
                                                            )}
                                                        />
                                                    </FormControl>
                                                </Form.Group>
                                                {selectedConnectionDestinationType !==
                                                    "" &&
                                                    connectionDestinationList &&
                                                    connectionDestinationList.filter(
                                                        (d) =>
                                                            (d.destination_type_code ||
                                                                d.type) ===
                                                            selectedConnectionDestinationType,
                                                    ).length > 0 && (
                                                        <Form.Group
                                                            className="w-full mb-3"
                                                            controlId="destination-edit-select"
                                                        >
                                                            <FormControl
                                                                className="w-full"
                                                                error={
                                                                    isDestinationError
                                                                }
                                                            >
                                                                <Select
                                                                    label={
                                                                        language
                                                                            ?.connection_detail
                                                                            ?.select_destination ??
                                                                        "Select Destination"
                                                                    }
                                                                    onChange={
                                                                        handleChangeDestination
                                                                    }
                                                                    value={
                                                                        selectedConnectionDestination
                                                                    }
                                                                >
                                                                    {connectionDestinationList
                                                                        .filter(
                                                                            (
                                                                                d,
                                                                            ) =>
                                                                                (d.destination_type_code ||
                                                                                    d.type) ===
                                                                                selectedConnectionDestinationType,
                                                                        )
                                                                        .map(
                                                                            (
                                                                                item,
                                                                            ) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        item.id
                                                                                    }
                                                                                    value={
                                                                                        item.id
                                                                                    }
                                                                                >
                                                                                    {item.name ||
                                                                                        item.type}
                                                                                </MenuItem>
                                                                            ),
                                                                        )}
                                                                </Select>
                                                                {isDestinationError && (
                                                                    <FormHelperText
                                                                        error
                                                                    >
                                                                        {language
                                                                            ?.connection_detail
                                                                            ?.select_destination ??
                                                                            "Please select a destination"}
                                                                    </FormHelperText>
                                                                )}
                                                            </FormControl>
                                                        </Form.Group>
                                                    )}
                                                <Box
                                                    position="relative"
                                                    className="w-full"
                                                >
                                                    {isDestinationSaveLoading && (
                                                        <div className="circle-loader">
                                                            <ClipLoader
                                                                color="#fff"
                                                                size={18}
                                                            />
                                                        </div>
                                                    )}
                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        onClick={
                                                            handleSaveDestinationEdit
                                                        }
                                                        type="button"
                                                        className="w-full"
                                                    >
                                                        Save
                                                    </Button>
                                                </Box>
                                            </>
                                        )}
                                    </CardContent>
                                </>
                            ) : (
                                <>
                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <Typography className="mr-3">
                                            <div className="item-icon-area">
                                                <LogoIcon
                                                    src={getDestinationIcon(
                                                        singleConnection
                                                            ?.destination?.type,
                                                    )}
                                                    className="lead-connection-icon"
                                                />
                                            </div>
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 18,
                                                fontWeight: "700",
                                                fontFamily:
                                                    "Nunito, sans-serif",
                                                color: isDestinationExpired
                                                    ? "red"
                                                    : "black",
                                            }}
                                        >
                                            {getDestinationName(
                                                singleConnection?.destination
                                                    ?.type,
                                            )}
                                        </Typography>
                                        {isDestinationExpired && (
                                            <SourceErrorTooltip
                                                placement="top"
                                                style={{ marginLeft: "10px" }}
                                                title={
                                                    <React.Fragment>
                                                        <Typography color="white">
                                                            {"Error"}
                                                        </Typography>
                                                        <span>
                                                            {
                                                                "Since the Access Token is expired, you must reconnect the destination."
                                                            }
                                                        </span>
                                                    </React.Fragment>
                                                }
                                            >
                                                <WarningIcon
                                                    style={{
                                                        verticalAlign: "sub",
                                                        marginLeft: "10px",
                                                        color: "red",
                                                    }}
                                                />
                                            </SourceErrorTooltip>
                                        )}
                                    </CardContent>
                                    <CardContent
                                        style={{ padding: "25px 20px" }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <div className="lead-service-info-area">
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.connections
                                                        ?.name ?? "Name"}
                                                </span>
                                                {
                                                    singleConnection
                                                        ?.destination?.name
                                                }
                                            </span>
                                        </div>
                                    </CardContent>
                                    {isDestinationExpired && (
                                        <ConnectionDetailReconnectButton
                                            reconnectType={
                                                singleConnection?.destination
                                                    ?.type
                                            }
                                            destinationId={
                                                singleConnection?.destination
                                                    ?.id
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </Card>
                    </Grid>
                </Grid>
                {editFilterOptions && (
                    <LeadConnectionFilter
                        operators={editFilterOptions?.operators}
                        fields={singleConnection?.template?.field_mappings?.[0]?.mappings?.map(
                            (x) => ({
                                label: x?.source_label || x?.source_field,
                                value: x?.source_field,
                            }),
                        )}
                        functions={editFilterOptions?.functions}
                        handleSave={handleFiltersSave}
                        filters={connectionFilters}
                        selectedOperator={selectedOperator}
                        isWizard={false}
                        onClose={filterOnClose}
                        filterShowStatus={filterShowStatus}
                        setFilterShowStatus={setFilterShowStatus}
                        isSaveLoading={isSaveFilterLoading}
                    ></LeadConnectionFilter>
                )}

                <Divider sx={{ my: 2 }} />

                <Grid
                    container
                    columnSpacing={{ xs: 12, sm: 12, md: 12 }}
                    className="trackers-area my-0"
                >
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Box sx={{ width: "100%", mt: 2 }}>
                            <ConnectionSectionHeader
                                title={
                                    language?.connection_detail?.mapping ??
                                    "Mapping"
                                }
                                description="Map source fields to destination fields so each lead is sent with the right data."
                                action={
                                    <>
                                        {fieldEditPermission &&
                                            singleConnection?.connection_detail
                                                ?.id &&
                                            singleConnection?.template
                                                ?.field_mappings?.[0]?.id && (
                                                <Tooltip
                                                    title={
                                                        language
                                                            ?.connection_detail
                                                            ?.export_mapping ??
                                                        "Export"
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            onClick={
                                                                handleExportMapping
                                                            }
                                                            disabled={
                                                                isExportingMapping
                                                            }
                                                            aria-label={
                                                                language
                                                                    ?.connection_detail
                                                                    ?.export_mapping ??
                                                                "Export"
                                                            }
                                                        >
                                                            {isExportingMapping ? (
                                                                <CircularProgress
                                                                    size={20}
                                                                />
                                                            ) : (
                                                                <FileDownloadOutlinedIcon />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        {fieldEditPermission ? (
                                            <StandartButton
                                                variant={
                                                    ButtonVariant.BLACK_OUTLINED
                                                }
                                                onClick={handleClickCancelBtn}
                                            >
                                                {language?.connection_detail
                                                    ?.cancel ?? "Cancel"}
                                            </StandartButton>
                                        ) : (
                                            <a
                                                style={{ padding: "10px" }}
                                                href="javascript:;"
                                                onClick={
                                                    isReloadingFields
                                                        ? undefined
                                                        : handleClickFieldEditBtn
                                                }
                                            >
                                                {isReloadingFields ? (
                                                    <CircularProgress
                                                        size={20}
                                                    />
                                                ) : (
                                                    matchFields.length > 0 && (
                                                        <EditOutlinedIcon />
                                                    )
                                                )}
                                            </a>
                                        )}
                                    </>
                                }
                            />
                        </Box>
                    </Grid>
                </Grid>
                {isDynamicValueOpen && (
                    <CreateDynamicValueModal
                        isOpen={isDynamicValueOpen}
                        onClose={() => setIsDynamicValueOpen(false)}
                        onSubmit={onDynamicVariableCreate}
                        availableSourceFields={sourceFieldOptions.map(
                            (field) => ({
                                value: field.value?.toString(),
                                label: field.label,
                            }),
                        )}
                        allFunctions={newFunctionsOptions}
                        editedField={undefined}
                        editFieldIndex={dynamicValueIndex}
                        notAvailableFieldNames={matchFields
                            ?.map((field) => {
                                return (
                                    field?.dynamic_field_name ||
                                    field?.name ||
                                    field?.sourceKey ||
                                    field?.source_field
                                );
                            })
                            .filter((name) => name)}
                    />
                )}
                <Grid
                    container
                    columnSpacing={{ xs: 12, sm: 12, md: 12 }}
                    className="trackers-area mt-4 mb-0"
                >
                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Box
                            className="tour-connection-mapping-section"
                            sx={{
                                width: "100%",
                                position: "relative",
                                ...(fieldEditPermission && {
                                    borderRadius: "10px",
                                    border: "2px solid #f66700",
                                    p: { xs: 1.5, md: 2 },
                                    pb: { xs: 5, md: 6 },
                                    backgroundColor: "#fff",
                                }),
                            }}
                        >
                            {matchFields.length > 0 && (
                                <Stack gap={1.5}>
                                    {matchFields.map((item, rowIndex) => {
                                        if (item?.isDynamicGroup === true) {
                                            const firstField =
                                                item.fields?.[0] || {};
                                            return (
                                                <MappingRowShell
                                                    key={rowIndex}
                                                    sourceCaption="Dynamic Value"
                                                    canDelete={
                                                        fieldEditPermission
                                                    }
                                                    onDelete={() =>
                                                        handleFieldRemove(
                                                            rowIndex,
                                                        )
                                                    }
                                                    source={
                                                        <MappingDynamicSource
                                                            name={
                                                                item.dynamicFieldName
                                                            }
                                                            mode={
                                                                item.dynamicMode
                                                            }
                                                            fieldCount={
                                                                item.fields
                                                                    ?.length ||
                                                                0
                                                            }
                                                        />
                                                    }
                                                    options={
                                                        fieldEditPermission ? (
                                                            <StandartButton
                                                                variant={
                                                                    ButtonVariant.BLACK_OUTLINED
                                                                }
                                                                onClick={() =>
                                                                    handleEditDynamicValue(
                                                                        rowIndex,
                                                                    )
                                                                }
                                                            >
                                                                Edit
                                                            </StandartButton>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "inline-flex",
                                                                }}
                                                                onMouseEnter={(
                                                                    e,
                                                                ) =>
                                                                    setOptionsHover(
                                                                        {
                                                                            index: rowIndex,
                                                                            anchorEl:
                                                                                e.currentTarget,
                                                                        },
                                                                    )
                                                                }
                                                                onMouseLeave={() =>
                                                                    setOptionsHover(
                                                                        null,
                                                                    )
                                                                }
                                                            >
                                                                <StandartButton
                                                                    variant={
                                                                        ButtonVariant.BLACK_OUTLINED
                                                                    }
                                                                    disabled
                                                                >
                                                                    {language
                                                                        ?.connection_detail
                                                                        ?.options ??
                                                                        "Options"}
                                                                </StandartButton>
                                                                <SummaryPopover
                                                                    open={
                                                                        optionsHover?.index ===
                                                                        rowIndex
                                                                    }
                                                                    anchorEl={
                                                                        optionsHover?.anchorEl ??
                                                                        null
                                                                    }
                                                                    onClose={() =>
                                                                        setOptionsHover(
                                                                            null,
                                                                        )
                                                                    }
                                                                >
                                                                    <SummaryPopoverSection
                                                                        label={
                                                                            language
                                                                                ?.lead
                                                                                ?.source_fields ??
                                                                            "Source Fields"
                                                                        }
                                                                        isLast
                                                                    >
                                                                        {item?.fields?.map(
                                                                            (
                                                                                field,
                                                                                fieldIndex,
                                                                            ) => (
                                                                                <SummaryPopoverValue
                                                                                    key={
                                                                                        fieldIndex
                                                                                    }
                                                                                    copyable
                                                                                >
                                                                                    {field?.source_label ||
                                                                                        field?.source_field}
                                                                                </SummaryPopoverValue>
                                                                            ),
                                                                        )}
                                                                    </SummaryPopoverSection>
                                                                </SummaryPopover>
                                                            </Box>
                                                        )
                                                    }
                                                    destination={
                                                        fieldEditPermission ? (
                                                            <FormControl className="w-full">
                                                                <Autocomplete
                                                                    id={`destination-field-autocomplete-${rowIndex}`}
                                                                    key={`destination-field-group-${rowIndex}-${firstField?.destination_field || ""}`}
                                                                    size="medium"
                                                                    options={[
                                                                        {
                                                                            label:
                                                                                firstField?.destination_label ||
                                                                                firstField?.destination_field ||
                                                                                "",
                                                                            value:
                                                                                firstField?.destination_field ||
                                                                                "",
                                                                        },
                                                                        ...(memoizedDestinationFields ||
                                                                            []),
                                                                    ].filter(
                                                                        (x) =>
                                                                            x?.label !==
                                                                            "",
                                                                    )}
                                                                    filterOptions={
                                                                        destinationFilterOptions
                                                                    }
                                                                    getOptionKey={(
                                                                        option,
                                                                    ) =>
                                                                        option?.value ||
                                                                        ""
                                                                    }
                                                                    isOptionEqualToValue={(
                                                                        option,
                                                                        value,
                                                                    ) =>
                                                                        option?.value ===
                                                                        value?.value
                                                                    }
                                                                    getOptionLabel={(
                                                                        option,
                                                                    ) =>
                                                                        option.label ||
                                                                        ""
                                                                    }
                                                                    value={
                                                                        memoizedDestinationFields?.find(
                                                                            (
                                                                                field,
                                                                            ) =>
                                                                                field.value ===
                                                                                (item.destination_field ||
                                                                                    firstField?.destination_field),
                                                                        ) || {
                                                                            label:
                                                                                item.destination_label ||
                                                                                firstField?.destination_label ||
                                                                                firstField?.destination_field ||
                                                                                "",
                                                                            value:
                                                                                item.destination_field ||
                                                                                firstField?.destination_field ||
                                                                                "",
                                                                        }
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                        newValue,
                                                                    ) => {
                                                                        const updatedMatchFields =
                                                                            matchFields.map(
                                                                                (
                                                                                    matchItem,
                                                                                    idx,
                                                                                ) => {
                                                                                    if (
                                                                                        idx ===
                                                                                            rowIndex &&
                                                                                        matchItem?.isDynamicGroup ===
                                                                                            true
                                                                                    ) {
                                                                                        return {
                                                                                            ...matchItem,
                                                                                            destination_field:
                                                                                                newValue?.value ||
                                                                                                "",
                                                                                            destination_label:
                                                                                                newValue?.label ||
                                                                                                "",
                                                                                            fields: matchItem.fields.map(
                                                                                                (
                                                                                                    field,
                                                                                                ) => ({
                                                                                                    ...field,
                                                                                                    destination_field:
                                                                                                        newValue?.value ||
                                                                                                        "",
                                                                                                    destination_label:
                                                                                                        newValue?.label ||
                                                                                                        "",
                                                                                                }),
                                                                                            ),
                                                                                        };
                                                                                    }
                                                                                    return matchItem;
                                                                                },
                                                                            );
                                                                        setMatchFields(
                                                                            updatedMatchFields,
                                                                        );
                                                                    }}
                                                                    renderInput={(
                                                                        params,
                                                                    ) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label={
                                                                                language
                                                                                    ?.connection_detail
                                                                                    ?.select_field ||
                                                                                "Select Field"
                                                                            }
                                                                            variant="outlined"
                                                                            sx={
                                                                                mappingAutocompleteTextFieldSx
                                                                            }
                                                                        />
                                                                    )}
                                                                    noOptionsText={
                                                                        <Box
                                                                            textAlign="center"
                                                                            width="100%"
                                                                        >
                                                                            <Typography variant="body2">
                                                                                No
                                                                                fields
                                                                                available
                                                                                for
                                                                                mapping.
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                />
                                                            </FormControl>
                                                        ) : (
                                                            <Typography
                                                                sx={
                                                                    mappingReadonlyFieldSx
                                                                }
                                                            >
                                                                <StandardText
                                                                    text={
                                                                        firstField?.destination_label ||
                                                                        firstField?.destination_field ||
                                                                        ""
                                                                    }
                                                                />
                                                            </Typography>
                                                        )
                                                    }
                                                />
                                            );
                                        }

                                        return (
                                            <MappingRowShell
                                                key={rowIndex}
                                                canDelete={fieldEditPermission}
                                                onDelete={() =>
                                                    handleFieldRemove(rowIndex)
                                                }
                                                source={
                                                    fieldEditPermission ? (
                                                        matchFields[rowIndex]
                                                            ?.is_static ? (
                                                            <FormControl
                                                                className="w-full"
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <LeadStaticField>
                                                                    <TextField
                                                                        label={
                                                                            language
                                                                                ?.lead
                                                                                ?.custom_field ??
                                                                            "Custom field"
                                                                        }
                                                                        variant="outlined"
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            handleSourceFieldChange(
                                                                                e,
                                                                                rowIndex,
                                                                            )
                                                                        }
                                                                        value={
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .source_field
                                                                        }
                                                                        error={isNumber(
                                                                            fieldMappingErrorIndexes?.notFilledSourceIndexes?.find(
                                                                                (
                                                                                    index,
                                                                                ) =>
                                                                                    index ===
                                                                                    rowIndex,
                                                                            ),
                                                                        )}
                                                                        helperText={
                                                                            isNumber(
                                                                                fieldMappingErrorIndexes?.notFilledSourceIndexes?.find(
                                                                                    (
                                                                                        index,
                                                                                    ) =>
                                                                                        index ===
                                                                                        rowIndex,
                                                                                ),
                                                                            ) &&
                                                                            "Fill out the field."
                                                                        }
                                                                        sx={
                                                                            mappingAutocompleteTextFieldSx
                                                                        }
                                                                    />
                                                                </LeadStaticField>
                                                            </FormControl>
                                                        ) : (
                                                            <FormControl
                                                                className="w-full"
                                                                error={isNumber(
                                                                    fieldMappingErrorIndexes?.notFilledSourceIndexes?.find(
                                                                        (
                                                                            index,
                                                                        ) =>
                                                                            index ===
                                                                            rowIndex,
                                                                    ),
                                                                )}
                                                            >
                                                                <Autocomplete
                                                                    id={`source-field-autocomplete-${rowIndex}`}
                                                                    key={`source-field-${rowIndex}-${matchFields[rowIndex]?.source_field || ""}`}
                                                                    options={
                                                                        sourceFieldOptions?.filter(
                                                                            (
                                                                                optionItem,
                                                                            ) =>
                                                                                optionItem.value !==
                                                                                "-",
                                                                        ) || []
                                                                    }
                                                                    getOptionKey={(
                                                                        option,
                                                                    ) =>
                                                                        option?.value ||
                                                                        ""
                                                                    }
                                                                    isOptionEqualToValue={(
                                                                        option,
                                                                        value,
                                                                    ) =>
                                                                        option?.value ===
                                                                        value?.value
                                                                    }
                                                                    size="medium"
                                                                    getOptionLabel={(
                                                                        option,
                                                                    ) =>
                                                                        option.label ||
                                                                        ""
                                                                    }
                                                                    renderOption={(
                                                                        props,
                                                                        option,
                                                                    ) => (
                                                                        <li
                                                                            {...props}
                                                                            key={
                                                                                option.value
                                                                            }
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    justifyContent:
                                                                                        "space-between",
                                                                                    width: "100%",
                                                                                }}
                                                                            >
                                                                                <span>
                                                                                    {option.label ||
                                                                                        ""}
                                                                                </span>
                                                                                {option.isDynamic && (
                                                                                    <PodcastsOutlinedIcon
                                                                                        sx={{
                                                                                            fontSize: 18,
                                                                                            color: "#ed6c02",
                                                                                            ml: 1,
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        </li>
                                                                    )}
                                                                    value={
                                                                        sourceFieldOptions?.find(
                                                                            (
                                                                                field,
                                                                            ) =>
                                                                                field.value ===
                                                                                matchFields[
                                                                                    rowIndex
                                                                                ]
                                                                                    .source_field,
                                                                        ) || {
                                                                            label:
                                                                                matchFields[
                                                                                    rowIndex
                                                                                ]
                                                                                    .source_label ||
                                                                                matchFields[
                                                                                    rowIndex
                                                                                ]
                                                                                    .source_field,
                                                                            value: matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .source_field,
                                                                        }
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                        newValue,
                                                                    ) => {
                                                                        handleSourceFieldSelect(
                                                                            {
                                                                                target: {
                                                                                    value:
                                                                                        newValue?.value ||
                                                                                        "",
                                                                                },
                                                                            },
                                                                            rowIndex,
                                                                        );
                                                                    }}
                                                                    renderInput={(
                                                                        params,
                                                                    ) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label={
                                                                                language
                                                                                    ?.connection_detail
                                                                                    ?.select_field ||
                                                                                "Select Field"
                                                                            }
                                                                            variant="outlined"
                                                                            helperText={
                                                                                Number.isInteger(
                                                                                    fieldMappingErrorIndexes?.notFilledSourceIndexes?.find(
                                                                                        (
                                                                                            index,
                                                                                        ) =>
                                                                                            index ===
                                                                                            rowIndex,
                                                                                    ),
                                                                                ) &&
                                                                                "Fill out the field."
                                                                            }
                                                                            sx={
                                                                                mappingAutocompleteTextFieldSx
                                                                            }
                                                                        />
                                                                    )}
                                                                />
                                                                {isNumber(
                                                                    fieldMappingErrorIndexes?.notFilledSourceIndexes?.find(
                                                                        (
                                                                            index,
                                                                        ) =>
                                                                            index ===
                                                                            rowIndex,
                                                                    ),
                                                                ) && (
                                                                    <FormHelperText>
                                                                        Fill out
                                                                        the
                                                                        field.
                                                                    </FormHelperText>
                                                                )}
                                                            </FormControl>
                                                        )
                                                    ) : (
                                                        <Typography
                                                            sx={
                                                                mappingReadonlyFieldSx
                                                            }
                                                        >
                                                            <StandardText
                                                                text={
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .source_label ||
                                                                    (matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .source_field ===
                                                                        "-" ||
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .source_field ===
                                                                        "" ? (
                                                                        <i className="text-muted">
                                                                            Empty
                                                                        </i>
                                                                    ) : (
                                                                        matchFields[
                                                                            rowIndex
                                                                        ]
                                                                            .source_field
                                                                    ))
                                                                }
                                                                copyable={
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .source_field !==
                                                                        "" &&
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .source_field !==
                                                                        "-"
                                                                }
                                                            />
                                                        </Typography>
                                                    )
                                                }
                                                options={
                                                    <>
                                                        <StandartButton
                                                            variant={
                                                                ButtonVariant.BLACK_OUTLINED
                                                            }
                                                            onClick={() => {
                                                                if (
                                                                    !fieldEditPermission
                                                                )
                                                                    return;
                                                                setOptionsHover(
                                                                    null,
                                                                );
                                                                handleOpenOptions(
                                                                    rowIndex,
                                                                );
                                                            }}
                                                            onMouseEnter={(e) =>
                                                                setOptionsHover(
                                                                    {
                                                                        index: rowIndex,
                                                                        anchorEl:
                                                                            e.currentTarget,
                                                                    },
                                                                )
                                                            }
                                                            onMouseLeave={() =>
                                                                setOptionsHover(
                                                                    null,
                                                                )
                                                            }
                                                            sx={{
                                                                position:
                                                                    "relative",
                                                            }}
                                                        >
                                                            {language
                                                                ?.connection_detail
                                                                ?.options ??
                                                                "Options"}
                                                            {badgeCreator(
                                                                matchFields[
                                                                    rowIndex
                                                                ],
                                                            ) > 0 && (
                                                                <Chip
                                                                    onClick={() => {}}
                                                                    label={badgeCreator(
                                                                        matchFields[
                                                                            rowIndex
                                                                        ],
                                                                    )}
                                                                    size="small"
                                                                    sx={{
                                                                        position:
                                                                            "absolute",
                                                                        top: -8,
                                                                        right: -8,
                                                                        height: 20,
                                                                        minWidth: 20,
                                                                        fontSize:
                                                                            "0.75rem",
                                                                        backgroundColor:
                                                                            "error.main",
                                                                        color: "white",
                                                                        "& .MuiChip-label":
                                                                            {
                                                                                px: 0.5,
                                                                            },
                                                                    }}
                                                                />
                                                            )}
                                                        </StandartButton>
                                                        <FieldOptionsSummaryPopover
                                                            open={
                                                                optionsHover?.index ===
                                                                rowIndex
                                                            }
                                                            anchorEl={
                                                                optionsHover?.anchorEl ??
                                                                null
                                                            }
                                                            item={{
                                                                options: {
                                                                    functions:
                                                                        item.fn ||
                                                                        [],
                                                                    default:
                                                                        item.default,
                                                                    prefix: item.prefix,
                                                                },
                                                                compared_fields:
                                                                    item.compared_fields,
                                                                sourceKey:
                                                                    item.source_field,
                                                            }}
                                                            allFunctions={
                                                                newFunctionsOptions
                                                            }
                                                            functionValues={{
                                                                maxLen: functionsMaxLenValues[
                                                                    rowIndex
                                                                ],
                                                                padWithLength:
                                                                    functionsPadWithValues[
                                                                        rowIndex
                                                                    ],
                                                                padWithCharacter:
                                                                    functionsPadWithCharacter[
                                                                        rowIndex
                                                                    ],
                                                                left: functionsLeftValues[
                                                                    rowIndex
                                                                ],
                                                                right: functionsRightValues[
                                                                    rowIndex
                                                                ],
                                                            }}
                                                            language={language}
                                                        />
                                                        {fieldEditPermission && (
                                                            <FieldOptionsModal
                                                                open={Boolean(
                                                                    item.options
                                                                        ?.isOpen,
                                                                )}
                                                                onClose={() =>
                                                                    handleOpenOptions(
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                item={{
                                                                    options: {
                                                                        functions:
                                                                            item.fn ||
                                                                            [],
                                                                        default:
                                                                            item.default,
                                                                        prefix: item.prefix,
                                                                    },
                                                                    compared_fields:
                                                                        item.compared_fields,
                                                                    sourceKey:
                                                                        item.source_field,
                                                                }}
                                                                index={rowIndex}
                                                                language={
                                                                    language
                                                                }
                                                                allFunctions={
                                                                    newFunctionsOptions
                                                                }
                                                                onFunctionsChange={(
                                                                    values,
                                                                ) =>
                                                                    handleFieldFunctionsChange(
                                                                        rowIndex,
                                                                        values,
                                                                    )
                                                                }
                                                                functionValues={{
                                                                    maxLen: functionsMaxLenValues[
                                                                        rowIndex
                                                                    ],
                                                                    padWithLength:
                                                                        functionsPadWithValues[
                                                                            rowIndex
                                                                        ],
                                                                    padWithCharacter:
                                                                        functionsPadWithCharacter[
                                                                            rowIndex
                                                                        ],
                                                                    left: functionsLeftValues[
                                                                        rowIndex
                                                                    ],
                                                                    right: functionsRightValues[
                                                                        rowIndex
                                                                    ],
                                                                }}
                                                                onFunctionValueChange={(
                                                                    key,
                                                                    value,
                                                                ) =>
                                                                    handleFunctionValueChange(
                                                                        rowIndex,
                                                                        key,
                                                                        value,
                                                                    )
                                                                }
                                                                onDefaultChange={(
                                                                    value,
                                                                ) =>
                                                                    handleAddedDefaults(
                                                                        {
                                                                            target: {
                                                                                value,
                                                                            },
                                                                        },
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                onPrefixChange={(
                                                                    value,
                                                                ) =>
                                                                    handleAddedPrefix(
                                                                        {
                                                                            target: {
                                                                                value,
                                                                            },
                                                                        },
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                onManageComparisons={() =>
                                                                    handleClickComparisationPart(
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                comparisonDisabled={
                                                                    !matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        ?.source_field
                                                                }
                                                                sourceFieldName={
                                                                    item.source_label ||
                                                                    item.source_field ||
                                                                    undefined
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                }
                                                destination={
                                                    fieldEditPermission ? (
                                                        <FormControl
                                                            className="w-full"
                                                            error={isNumber(
                                                                fieldMappingErrorIndexes?.notFilledDestinationIndexes?.find(
                                                                    (index) =>
                                                                        index ===
                                                                        rowIndex,
                                                                ),
                                                            )}
                                                        >
                                                            <Autocomplete
                                                                id={`destination-field-autocomplete-${rowIndex}`}
                                                                key={`destination-field-${rowIndex}-${matchFields[rowIndex]?.destination_field || ""}`}
                                                                size="medium"
                                                                options={[
                                                                    {
                                                                        label:
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .destination_label ||
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .destination_field,
                                                                        value: matchFields[
                                                                            rowIndex
                                                                        ]
                                                                            .destination_field,
                                                                    },
                                                                    ...(memoizedDestinationFields ||
                                                                        []),
                                                                ].filter(
                                                                    (x) =>
                                                                        x?.label !==
                                                                        "",
                                                                )}
                                                                filterOptions={
                                                                    destinationFilterOptions
                                                                }
                                                                getOptionKey={(
                                                                    option,
                                                                ) =>
                                                                    option?.value ||
                                                                    ""
                                                                }
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value,
                                                                ) =>
                                                                    option?.value ===
                                                                    value?.value
                                                                }
                                                                getOptionLabel={(
                                                                    option,
                                                                ) =>
                                                                    option.label ||
                                                                    ""
                                                                }
                                                                value={
                                                                    memoizedDestinationFields?.find(
                                                                        (
                                                                            field,
                                                                        ) =>
                                                                            field.value ===
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .destination_field,
                                                                    ) || {
                                                                        label:
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .destination_label ||
                                                                            matchFields[
                                                                                rowIndex
                                                                            ]
                                                                                .destination_field,
                                                                        value: matchFields[
                                                                            rowIndex
                                                                        ]
                                                                            .destination_field,
                                                                    }
                                                                }
                                                                onChange={(
                                                                    event,
                                                                    newValue,
                                                                ) =>
                                                                    handleDestinationFieldSelect(
                                                                        newValue?.value ||
                                                                            "",
                                                                        rowIndex,
                                                                    )
                                                                }
                                                                renderInput={(
                                                                    params,
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={
                                                                            language
                                                                                ?.connection_detail
                                                                                ?.select_field ||
                                                                            "Select Field"
                                                                        }
                                                                        variant="outlined"
                                                                        sx={
                                                                            mappingAutocompleteTextFieldSx
                                                                        }
                                                                    />
                                                                )}
                                                                noOptionsText={
                                                                    <Box
                                                                        textAlign="center"
                                                                        width="100%"
                                                                    >
                                                                        <Typography variant="body2">
                                                                            No
                                                                            fields
                                                                            available
                                                                            for
                                                                            mapping.
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                            {isNumber(
                                                                fieldMappingErrorIndexes?.notFilledDestinationIndexes?.find(
                                                                    (index) =>
                                                                        index ===
                                                                        rowIndex,
                                                                ),
                                                            ) && (
                                                                <FormHelperText>
                                                                    Fill out the
                                                                    field.
                                                                </FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    ) : (
                                                        <Typography
                                                            sx={
                                                                mappingReadonlyFieldSx
                                                            }
                                                        >
                                                            <StandardText
                                                                text={
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .destination_label ||
                                                                    matchFields[
                                                                        rowIndex
                                                                    ]
                                                                        .destination_field
                                                                }
                                                            />
                                                        </Typography>
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                </Stack>
                            )}
                            {![...matchFields]?.length && (
                                <EmptyState
                                    flush={fieldEditPermission}
                                    icon={<SwapHorizOutlinedIcon />}
                                    title="No mappings yet"
                                    description="Map source fields to destination fields so each lead is sent with the right data."
                                    actionLabel="Add your first mapping"
                                    actionDisabled={isReloadingFields}
                                    onAction={async () => {
                                        if (!fieldEditPermission) {
                                            await handleClickFieldEditBtn();
                                        }
                                        handleFieldAdd();
                                    }}
                                />
                            )}
                            {fieldEditPermission &&
                                matchFields.length > 0 &&
                                matchFields.length < 5000 && (
                                    <Stack
                                        justifyContent="space-between"
                                        alignItems="center"
                                        direction="row"
                                        gap={1}
                                        marginTop={3}
                                        flexWrap="wrap"
                                        width="100%"
                                    >
                                        <Stack
                                            direction="row"
                                            gap={1}
                                            alignItems="center"
                                            flexWrap="wrap"
                                        >
                                            <Tooltip title="You can select a source field from the list which are fetched from the source.">
                                                <Box>
                                                    <StandartButton
                                                        variant={
                                                            ButtonVariant.BLACK_OUTLINED
                                                        }
                                                        onClick={handleFieldAdd}
                                                    >
                                                        {"Add Value"}
                                                    </StandartButton>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title="You may define and assign a static source value through manual input.">
                                                <Box>
                                                    <StandartButton
                                                        variant={
                                                            ButtonVariant.BLACK_OUTLINED
                                                        }
                                                        onClick={
                                                            handleStaticFieldAdd
                                                        }
                                                    >
                                                        <span>
                                                            {language?.buttons
                                                                ?.add_a_static_value ??
                                                                "Add a Static Value"}
                                                        </span>
                                                    </StandartButton>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title="You can add a dynamic value to the field, dynamic value may contain multiple fields and functions.">
                                                <Box>
                                                    <StandartButton
                                                        variant={
                                                            ButtonVariant.BLACK_OUTLINED
                                                        }
                                                        onClick={() => {
                                                            setDynamicValueIndex(
                                                                null,
                                                            );
                                                            setIsDynamicValueOpen(
                                                                true,
                                                            );
                                                        }}
                                                    >
                                                        Add a Dynamic Value
                                                    </StandartButton>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                        {!isDynamicFieldsLoading &&
                                            dynamicFields.length > 0 && (
                                                <StandartButton
                                                    variant={
                                                        ButtonVariant.BLACK_OUTLINED
                                                    }
                                                    onClick={() =>
                                                        setIsDynamicFieldsModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    {language?.connection_detail
                                                        ?.dynamic_fields ??
                                                        "Dynamic Fields"}
                                                </StandartButton>
                                            )}
                                    </Stack>
                                )}

                            {fieldEditPermission && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        right: "15px",
                                        bottom: "-18px",
                                        zIndex: 1,
                                        width: "fit-content",
                                        "@media (max-width: 768px)": {
                                            position: "static",
                                            width: "100%",
                                            marginTop: "12px",
                                        },
                                    }}
                                >
                                    {isSaveMappingLoading && (
                                        <div className="circle-loader">
                                            <ClipLoader
                                                color={"#000"}
                                                size={17}
                                            />
                                        </div>
                                    )}
                                    <Button
                                        type="button"
                                        onClick={() =>
                                            matchFields?.length &&
                                            handleFielSaveChanges()
                                        }
                                        disabled={!matchFields?.length}
                                        color="primary"
                                        variant="contained"
                                    >
                                        {language?.connection_detail
                                            ?.save_changes ?? "Save Changes"}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </div>

            <DynamicFieldsSection
                open={isDynamicFieldsModalOpen}
                onClose={() => setIsDynamicFieldsModalOpen(false)}
                dynamicFields={dynamicFields}
                loading={isDynamicFieldsLoading}
                sourceFieldOptions={sourceFieldOptions
                    .filter((field) => !field.isDynamic)
                    .map((field) => ({
                        value: field.value?.toString(),
                        key: field.key?.toString?.() ?? field.value?.toString(),
                        type: field.type?.toString?.() ?? "",
                        label: field.label,
                    }))}
                allFunctions={newFunctionsOptions}
                language={language}
                onRefresh={() => {
                    fetchDynamicFields();
                    dispatch(getSingleConnectionRedux(params.id && params.id));
                }}
                onDynamicFieldDeleted={() => {
                    dynamicFieldDeletedDuringEditRef.current = true;
                }}
            />

            {isComparisationFieldModalOpen && (
                <AddComparisationModal
                    matchFields={matchFields}
                    setMatchFields={setMatchFields}
                    isOpen={isComparisationFieldModalOpen}
                    setIsOpen={setIsComparisationFieldModalOpen}
                    fieldIndex={comparisationFieldIndex}
                />
            )}
        </PageContainer>
    );
};

export default ConnectionDetail;
